import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hook to detect mobile screen
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

function Approvals({ token, user }) {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('pending');
  const [approvals, setApprovals] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [allApprovals, setAllApprovals] = useState([]);
  const [infoRequiredApprovals, setInfoRequiredApprovals] = useState([]);
  const [approvalConfig, setApprovalConfig] = useState(null);
  const [remarks, setRemarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Detail view state
  const [detailModal, setDetailModal] = useState({ show: false, approval: null });
  const [viewedAttachments, setViewedAttachments] = useState({});
  
  // AI Chat state
  const [aiChatModal, setAiChatModal] = useState({ show: false, repairId: null, conversations: [] });
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  
  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({ show: false, repairId: null, action: null, hasViewedAttachments: false });
  
  // Request additional info modal
  const [infoRequestModal, setInfoRequestModal] = useState({ show: false, repairId: null, reason: '' });
  
  // Info response modal (for responding to info requests)
  const [infoResponseModal, setInfoResponseModal] = useState({ 
    show: false, 
    approval: null, 
    response: '', 
    files: [],
    fileCategory: 'quote'
  });
  
  // Offline approval modal state
  const [offlineApprovalModal, setOfflineApprovalModal] = useState({ 
    show: false, 
    repairId: null,
    approverName: '',
    approvalMethod: 'whatsapp',
    approvalDate: new Date().toISOString().split('T')[0],
    remarks: '',
    screenshot: null
  });
  
  // PDF Viewer modal state
  const [pdfViewerModal, setPdfViewerModal] = useState({ show: false, data: null, filename: '', repairId: null, attachmentId: null });
  
  // Info History modal state
  const [infoHistoryModal, setInfoHistoryModal] = useState({ show: false, repair: null });
  
  // Phase 4: Invoice Tallying state
  const [workCompleteApprovals, setWorkCompleteApprovals] = useState([]);
  const [invoiceVarianceApprovals, setInvoiceVarianceApprovals] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  
  // Mark Work Complete modal
  const [workCompleteModal, setWorkCompleteModal] = useState({ 
    show: false, 
    repairId: null, 
    notes: '',
    workshopInDate: new Date().toISOString().split('T')[0]
  });
  
  // Upload Invoice modal
  const [invoiceModal, setInvoiceModal] = useState({ 
    show: false, 
    repair: null,
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    finalAmount: '',
    dueDate: '',
    workshopOutDate: new Date().toISOString().split('T')[0],
    invoiceFile: null
  });
  
  // Variance Resolution modal
  const [varianceModal, setVarianceModal] = useState({ 
    show: false, 
    repair: null,
    explanation: '',
    action: 'explain'
  });
  
  // Supplementary Repair modal
  const [supplementaryModal, setSupplementaryModal] = useState({ 
    show: false, 
    repairId: null,
    description: '',
    estimatedCost: '',
    quoteFiles: []
  });
  
  // Settings state
  const [editingConfig, setEditingConfig] = useState(false);
  const [configLevels, setConfigLevels] = useState([]);
  const [fleetSettings, setFleetSettings] = useState(null);
  const [editingFleetSettings, setEditingFleetSettings] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    vehicleNo: '',
    dateFrom: '',
    dateTo: '',
    costMin: '',
    costMax: '',
    approver: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [approvedApprovals, setApprovedApprovals] = useState([]);
  const [rejectedApprovals, setRejectedApprovals] = useState([]);

  useEffect(() => {
    fetchData();
    fetchApprovalConfig();
    fetchFleetSettings();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchPendingApprovals(),
        fetchMyRequests(),
        fetchAllApprovals(),
        fetchApprovedApprovals(),
        fetchRejectedApprovals(),
        fetchInfoRequiredApprovals(),
        fetchWorkCompleteApprovals(),
        fetchInvoiceVarianceApprovals(),
        fetchCompletedJobs()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/approvals/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setApprovals(data);
      }
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    }
  };

  const fetchMyRequests = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/approvals/my-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyRequests(data);
      }
    } catch (error) {
      console.error('Error fetching my requests:', error);
    }
  };

  const fetchAllApprovals = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/approvals/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllApprovals(data);
      }
    } catch (error) {
      console.error('Error fetching all approvals:', error);
    }
  };

  const fetchApprovedApprovals = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/approvals/approved`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setApprovedApprovals(data);
      }
    } catch (error) {
      console.error('Error fetching approved approvals:', error);
    }
  };

  const fetchRejectedApprovals = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/approvals/rejected`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRejectedApprovals(data);
      }
    } catch (error) {
      console.error('Error fetching rejected approvals:', error);
    }
  };

  const fetchInfoRequiredApprovals = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/approvals/info-required`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInfoRequiredApprovals(data);
      }
    } catch (error) {
      console.error('Error fetching info required approvals:', error);
    }
  };

  // Phase 4: Invoice Tallying fetch functions
  const fetchWorkCompleteApprovals = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/approvals/work-complete`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWorkCompleteApprovals(data);
      }
    } catch (error) {
      console.error('Error fetching work complete approvals:', error);
    }
  };

  const fetchInvoiceVarianceApprovals = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/approvals/invoice-variance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInvoiceVarianceApprovals(data);
      }
    } catch (error) {
      console.error('Error fetching invoice variance approvals:', error);
    }
  };

  const fetchCompletedJobs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/approvals/completed-jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompletedJobs(data);
      }
    } catch (error) {
      console.error('Error fetching completed jobs:', error);
    }
  };

  // Filter function
  const applyFilters = (approvalsList) => {
    return approvalsList.filter(approval => {
      // Vehicle filter
      if (filters.vehicleNo && !approval.vehicle_no?.toLowerCase().includes(filters.vehicleNo.toLowerCase())) {
        return false;
      }
      
      // Date range filter
      if (filters.dateFrom) {
        const approvalDate = new Date(approval.requested_at);
        const fromDate = new Date(filters.dateFrom);
        if (approvalDate < fromDate) return false;
      }
      if (filters.dateTo) {
        const approvalDate = new Date(approval.requested_at);
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59);
        if (approvalDate > toDate) return false;
      }
      
      // Cost range filter
      if (filters.costMin && approval.estimated_cost < parseFloat(filters.costMin)) {
        return false;
      }
      if (filters.costMax && approval.estimated_cost > parseFloat(filters.costMax)) {
        return false;
      }
      
      // Approver filter
      if (filters.approver) {
        const hasApprover = approval.approval_history?.some(h => 
          h.approved_by?.toLowerCase().includes(filters.approver.toLowerCase())
        );
        if (!hasApprover) return false;
      }
      
      return true;
    });
  };

  const clearFilters = () => {
    setFilters({
      vehicleNo: '',
      dateFrom: '',
      dateTo: '',
      costMin: '',
      costMax: '',
      approver: ''
    });
  };

  const fetchApprovalConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/approval-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setApprovalConfig(data);
        setConfigLevels(data.levels || []);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchFleetSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/fleet`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFleetSettings(data);
      }
    } catch (error) {
      console.error('Error fetching fleet settings:', error);
    }
  };

  const saveFleetSettings = async () => {
    try {
      const formData = new FormData();
      formData.append('vehicle_replacement_age_years', fleetSettings.vehicle_replacement_age_years);
      formData.append('default_daily_income', fleetSettings.default_daily_income);
      formData.append('coe_max_years', fleetSettings.coe_max_years);
      formData.append('maintenance_interval_months', fleetSettings.maintenance_interval_months);

      const response = await fetch(`${BACKEND_URL}/api/settings/fleet`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Fleet settings saved!' });
        setEditingFleetSettings(false);
        fetchFleetSettings();
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to save settings' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save fleet settings' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  // Track attachment view
  const recordAttachmentView = async (repairId, attachmentId) => {
    try {
      const formData = new FormData();
      formData.append('attachment_id', attachmentId);
      
      await fetch(`${BACKEND_URL}/api/repairs/${repairId}/view-attachment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      // Update local state
      setViewedAttachments(prev => ({
        ...prev,
        [repairId]: [...(prev[repairId] || []), attachmentId]
      }));
    } catch (error) {
      console.error('Error recording view:', error);
    }
  };

  // Check if user has viewed attachments for this repair
  const hasViewedAttachments = (repairId) => {
    return (viewedAttachments[repairId] || []).length > 0;
  };

  // Handle approval/rejection with confirmation
  const initiateAction = (repairId, action) => {
    const hasViewed = hasViewedAttachments(repairId);
    setConfirmModal({
      show: true,
      repairId,
      action,
      hasViewedAttachments: hasViewed
    });
  };

  const handleAction = async () => {
    const { repairId, action, hasViewedAttachments } = confirmModal;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/approvals/${repairId}/action`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action,
          remarks: remarks[repairId] || '',
          attachments_viewed: viewedAttachments[repairId] || []
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Show specific error message from backend
        setMessage({ type: 'error', text: data.detail || 'Action failed' });
        setTimeout(() => setMessage(null), 5000);
        return;
      }
      
      setMessage({ type: action === 'approve' ? 'success' : 'info', text: data.message });
      
      fetchData();
      setRemarks({ ...remarks, [repairId]: '' });
      setConfirmModal({ show: false, repairId: null, action: null, hasViewedAttachments: false });
      setDetailModal({ show: false, approval: null });
      
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Network error - please try again' });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  // Request additional info
  const requestAdditionalInfo = async () => {
    if (!infoRequestModal.reason.trim()) {
      setMessage({ type: 'error', text: 'Please provide a reason for requesting additional information' });
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('reason', infoRequestModal.reason);
      
      const response = await fetch(`${BACKEND_URL}/api/repairs/${infoRequestModal.repairId}/request-additional-info`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Additional information requested from requester' });
        fetchData();
        setInfoRequestModal({ show: false, repairId: null, reason: '' });
        setDetailModal({ show: false, approval: null });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to request additional info' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to request additional information' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  // Submit response to info request and resubmit for approval
  const submitInfoResponse = async () => {
    const { approval, response: responseText, files, fileCategory } = infoResponseModal;
    
    if (!responseText.trim() && files.length === 0) {
      setMessage({ type: 'error', text: 'Please provide a response or upload files' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('response', responseText || '');
      formData.append('file_category', fileCategory);
      
      // Add files
      for (const file of files) {
        formData.append('files', file);
      }
      
      const response = await fetch(`${BACKEND_URL}/api/repairs/${approval.repair_id}/respond-info-request`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const fileMsg = data.files_added > 0 ? ` (${data.files_added} file(s) added)` : '';
        setMessage({ type: 'success', text: `Response submitted and repair resubmitted for approval${fileMsg}` });
        fetchData();
        setInfoResponseModal({ show: false, approval: null, response: '', files: [], fileCategory: 'quote' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to submit response' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to submit response' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  // Submit offline approval
  const submitOfflineApproval = async () => {
    const { repairId, approverName, approvalMethod, approvalDate, remarks, screenshot } = offlineApprovalModal;
    
    if (!approverName.trim()) {
      setMessage({ type: 'error', text: 'Please enter the approver name' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('approver_name', approverName);
      formData.append('approval_method', approvalMethod);
      formData.append('approval_date', approvalDate);
      if (remarks) formData.append('remarks', remarks);
      if (screenshot) formData.append('screenshot', screenshot);
      
      const response = await fetch(`${BACKEND_URL}/api/approvals/${repairId}/offline-approval`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: `Offline approval recorded successfully${data.has_screenshot ? ' with screenshot' : ''}` });
        await fetchData();
        // Also close the detail modal to show updated data when reopened
        setDetailModal({ show: false, approval: null });
        setOfflineApprovalModal({ 
          show: false, 
          repairId: null,
          approverName: '',
          approvalMethod: 'whatsapp',
          approvalDate: new Date().toISOString().split('T')[0],
          remarks: '',
          screenshot: null
        });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to record offline approval' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to record offline approval' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  // Delete approval (super admin only)
  const deleteApproval = async (repairId) => {
    if (!window.confirm('Are you sure you want to delete this pending approval? The repair will be reset to draft status.')) {
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/approvals/${repairId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Approval deleted. Repair reset to draft.' });
        fetchData();
        setDetailModal({ show: false, approval: null });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to delete approval' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to delete approval' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  // ============================================
  // Phase 4: Invoice Tallying Functions
  // ============================================

  // Mark Work Complete
  const handleMarkWorkComplete = async () => {
    const { repairId, notes, workshopInDate } = workCompleteModal;
    
    if (!workshopInDate) {
      setMessage({ type: 'error', text: 'Workshop In Date is required' });
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('work_completion_notes', notes || '');
      formData.append('workshop_in_date', workshopInDate);
      
      const response = await fetch(`${BACKEND_URL}/api/repairs/${repairId}/mark-work-complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Repair marked as work complete - ready for invoice' });
        fetchData();
        setWorkCompleteModal({ show: false, repairId: null, notes: '', workshopInDate: new Date().toISOString().split('T')[0] });
        setDetailModal({ show: false, approval: null });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to mark work complete' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to mark work complete' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  // Upload Invoice
  const handleUploadInvoice = async () => {
    const { repair, invoiceNumber, invoiceDate, finalAmount, dueDate, workshopOutDate, invoiceFile } = invoiceModal;
    
    if (!invoiceNumber.trim()) {
      setMessage({ type: 'error', text: 'Please enter the invoice number' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    if (!finalAmount || parseFloat(finalAmount) <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid final amount' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    if (!workshopOutDate) {
      setMessage({ type: 'error', text: 'Please enter the workshop out date' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    if (!invoiceFile) {
      setMessage({ type: 'error', text: 'Please upload the invoice file' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('invoice_number', invoiceNumber);
      formData.append('invoice_date', invoiceDate);
      formData.append('final_amount', parseFloat(finalAmount));
      formData.append('workshop_out_date', workshopOutDate);
      if (dueDate) formData.append('due_date', dueDate);
      formData.append('invoice_file', invoiceFile);
      
      const response = await fetch(`${BACKEND_URL}/api/repairs/${repair.repair_id}/upload-invoice`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.requires_approval) {
          setMessage({ 
            type: 'info', 
            text: `Invoice uploaded. Variance of $${Math.abs(data.variance).toFixed(2)} requires Senior Manager approval.` 
          });
        } else if (data.requires_explanation) {
          setMessage({ 
            type: 'info', 
            text: `Invoice uploaded. Variance of $${Math.abs(data.variance).toFixed(2)} requires explanation.` 
          });
        } else {
          setMessage({ type: 'success', text: 'Invoice uploaded - Job completed!' });
        }
        fetchData();
        setInvoiceModal({ 
          show: false, 
          repair: null, 
          invoiceNumber: '', 
          invoiceDate: new Date().toISOString().split('T')[0],
          finalAmount: '',
          dueDate: '',
          workshopOutDate: new Date().toISOString().split('T')[0],
          invoiceFile: null 
        });
        setDetailModal({ show: false, approval: null });
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to upload invoice' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to upload invoice' });
    }
    setTimeout(() => setMessage(null), 5000);
  };

  // Resolve Variance
  const handleResolveVariance = async () => {
    const { repair, explanation, action } = varianceModal;
    
    if ((action === 'explain' || action === 'reject') && !explanation.trim()) {
      setMessage({ type: 'error', text: 'Please provide an explanation' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('action', action);
      if (explanation) formData.append('explanation', explanation);
      
      const response = await fetch(`${BACKEND_URL}/api/repairs/${repair.repair_id}/resolve-variance`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setMessage({ type: 'success', text: data.message });
        fetchData();
        setVarianceModal({ show: false, repair: null, explanation: '', action: 'explain' });
        setDetailModal({ show: false, approval: null });
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to resolve variance' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to resolve variance' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  // Create Supplementary Repair
  const handleCreateSupplementary = async () => {
    const { repairId, description, estimatedCost, quoteFiles } = supplementaryModal;
    
    if (!description.trim()) {
      setMessage({ type: 'error', text: 'Please enter a description for the supplementary work' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    if (!estimatedCost || parseFloat(estimatedCost) <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid estimated cost' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('description', description);
      formData.append('estimated_cost', parseFloat(estimatedCost));
      for (const file of quoteFiles) {
        formData.append('quotes', file);
      }
      
      const response = await fetch(`${BACKEND_URL}/api/repairs/${repairId}/supplementary`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: `Supplementary repair ${data.supplementary_id} created. Submit for approval from Repairs page.` 
        });
        fetchData();
        setSupplementaryModal({ show: false, repairId: null, description: '', estimatedCost: '', quoteFiles: [] });
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to create supplementary repair' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to create supplementary repair' });
    }
    setTimeout(() => setMessage(null), 5000);
  };

  // AI Chat functions
  const openAiChat = async (repairId) => {
    setAiChatModal({ show: true, repairId, conversations: [] });
    setAiLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/repairs/${repairId}/ai-conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAiChatModal(prev => ({ ...prev, conversations: data }));
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const sendAiQuestion = async () => {
    if (!aiQuestion.trim() || !aiChatModal.repairId) return;
    
    setAiLoading(true);
    const questionText = aiQuestion;
    setAiQuestion('');
    
    // Add pending message to UI
    setAiChatModal(prev => ({
      ...prev,
      conversations: [...prev.conversations, {
        id: 'pending',
        question: questionText,
        answer: null,
        asked_by: user?.email,
        asked_at: new Date().toISOString()
      }]
    }));
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/repairs/${aiChatModal.repairId}/ai-chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          repair_id: aiChatModal.repairId,
          question: questionText
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Replace pending message with actual response
        setAiChatModal(prev => ({
          ...prev,
          conversations: prev.conversations.map(c => 
            c.id === 'pending' 
              ? { ...c, id: data.conversation_id, answer: data.answer }
              : c
          )
        }));
      } else {
        setMessage({ type: 'error', text: 'Failed to get AI response' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to send question' });
    } finally {
      setAiLoading(false);
    }
  };

  const saveApprovalConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/approval-config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          levels: configLevels,
          require_all_levels_above_threshold: true
        })
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Configuration saved!' });
        setEditingConfig(false);
        fetchApprovalConfig();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save configuration' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const addLevel = () => {
    const newLevel = {
      level: configLevels.length + 1,
      role: 'manager',
      name: 'New Level',
      threshold_min: configLevels.length > 0 ? configLevels[configLevels.length - 1].threshold_max : 0,
      threshold_max: 50000
    };
    setConfigLevels([...configLevels, newLevel]);
  };

  const updateLevel = (index, field, value) => {
    const updated = [...configLevels];
    updated[index] = { ...updated[index], [field]: value };
    setConfigLevels(updated);
  };

  const removeLevel = (index) => {
    const updated = configLevels.filter((_, i) => i !== index);
    updated.forEach((level, i) => level.level = i + 1);
    setConfigLevels(updated);
  };

  // Calculate days pending
  const getDaysPending = (requestedAt) => {
    if (!requestedAt) return 0;
    const now = new Date();
    const requested = new Date(requestedAt);
    const diffTime = Math.abs(now - requested);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get urgency color based on days pending
  const getUrgencyColor = (days) => {
    if (days >= 7) return '#DC2626'; // Red - urgent
    if (days >= 3) return '#F59E0B'; // Orange - warning
    return '#64748B'; // Grey - normal
  };

  // Check if user is super admin
  const isSuperAdmin = !!user?.is_super_admin;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-pending',
      approved: 'badge-approved',
      rejected: 'badge-rejected',
      additional_info_required: 'badge-pending',
      draft: 'badge-active'
    };
    return badges[status] || 'badge-active';
  };

  // Compact list row for approvals
  const renderApprovalRow = (approval, showActions = false) => {
    const repair = approval.repair || {};
    const daysPending = getDaysPending(approval.requested_at);
    const urgencyColor = getUrgencyColor(daysPending);
    const currentLevel = approval.current_level || 1;
    const requiredLevels = approval.required_levels || [];
    const currentLevelName = requiredLevels.find(l => l.level === currentLevel)?.name || `Level ${currentLevel}`;
    
    return (
      <tr 
        key={approval.repair_id}
        style={{ cursor: 'pointer' }}
        onClick={() => setDetailModal({ show: true, approval })}
      >
        <td>
          <span className="vin-plate" style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem' }}>
            {approval.vehicle_no}
          </span>
        </td>
        <td>
          <strong style={{ fontSize: '0.875rem' }}>{repair.description}</strong>
          <div style={{ fontSize: '0.75rem', color: '#64748B' }}>
            by {approval.requested_by?.split('@')[0]}
          </div>
        </td>
        <td style={{ fontFamily: 'JetBrains Mono', fontWeight: '600' }}>
          {formatCurrency(approval.estimated_cost)}
        </td>
        <td>
          <div style={{ fontSize: '0.75rem', color: '#64748B' }}>
            {new Date(approval.requested_at).toLocaleDateString()}
          </div>
        </td>
        <td>
          <div style={{ 
            fontWeight: '700', 
            fontSize: '1rem', 
            color: urgencyColor,
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem'
          }}>
            {daysPending >= 7 && '🔴'}
            {daysPending >= 3 && daysPending < 7 && '🟠'}
            {daysPending} day{daysPending !== 1 ? 's' : ''}
          </div>
        </td>
        <td>
          <span className={`badge ${getStatusBadge(approval.status)}`} style={{ fontSize: '0.7rem' }}>
            {approval.status === 'additional_info_required' ? 'INFO REQ' : approval.status?.toUpperCase()}
          </span>
          <div style={{ fontSize: '0.7rem', color: '#64748B', marginTop: '0.25rem' }}>
            {currentLevelName}
          </div>
        </td>
        <td onClick={(e) => e.stopPropagation()}>
          {(showActions || isSuperAdmin) && approval.status === 'pending' && (
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button 
                className="btn btn-success"
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem' }}
                onClick={() => initiateAction(approval.repair_id, 'approve')}
              >
                ✓
              </button>
              <button 
                className="btn btn-secondary"
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem' }}
                onClick={() => openAiChat(approval.repair_id)}
              >
                AI
              </button>
              <button 
                className="btn btn-danger"
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem' }}
                onClick={() => initiateAction(approval.repair_id, 'reject')}
              >
                ✗
              </button>
              {isSuperAdmin && (
                <button 
                  className="btn"
                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', background: '#6B7280', color: 'white' }}
                  onClick={() => deleteApproval(approval.repair_id)}
                  title="Delete approval (reset to draft)"
                >
                  🗑️
                </button>
              )}
            </div>
          )}
        </td>
      </tr>
    );
  };

  // Mobile card renderer for approval lists (simple card)
  const renderMobileApprovalCard = (approval, showActions = false, tabType = 'pending') => {
    const repair = approval.repair || {};
    const daysPending = getDaysPending(approval.requested_at);
    const urgencyColor = getUrgencyColor(daysPending);
    const currentLevel = approval.current_level || 1;
    const requiredLevels = approval.required_levels || [];
    const currentLevelName = requiredLevels.find(l => l.level === currentLevel)?.name || `Level ${currentLevel}`;
    
    // Determine status badge color based on tab
    const getStatusColor = () => {
      switch(tabType) {
        case 'approved': return '#10B981';
        case 'work-complete': return '#8B5CF6';
        case 'completed': return '#059669';
        case 'rejected': return '#EF4444';
        case 'info-required': return '#F59E0B';
        default: return '#3B82F6';
      }
    };
    
    return (
      <div
        key={approval.repair_id}
        onClick={() => setDetailModal({ show: true, approval })}
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '1rem',
          marginBottom: '0.75rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #E2E8F0',
          cursor: 'pointer',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
      >
        {/* Header row: Vehicle + Status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <span className="vin-plate" style={{ fontSize: '0.75rem' }}>{approval.vehicle_no}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {tabType === 'pending' && (
              <span style={{ fontWeight: '700', fontSize: '0.8rem', color: urgencyColor }}>
                {daysPending >= 7 && '🔴'}{daysPending >= 3 && daysPending < 7 && '🟠'}{daysPending}d
              </span>
            )}
            <span style={{ 
              padding: '0.2rem 0.5rem', 
              borderRadius: '4px', 
              fontSize: '0.65rem',
              background: getStatusColor(),
              color: 'white'
            }}>
              {tabType.toUpperCase().replace('-', ' ')}
            </span>
          </div>
        </div>
        
        {/* Description */}
        <p style={{ fontSize: '0.85rem', fontWeight: '500', margin: '0 0 0.5rem 0', color: '#1E293B' }}>
          {repair.description || approval.repair?.description || 'N/A'}
        </p>
        
        {/* Meta row: Cost + Date */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <span style={{ fontFamily: 'JetBrains Mono', fontWeight: '600', fontSize: '0.9rem', color: '#003366' }}>
            {formatCurrency(approval.estimated_cost)}
          </span>
          <span style={{ fontSize: '0.7rem', color: '#64748B' }}>
            {new Date(approval.requested_at).toLocaleDateString()}
            {tabType === 'pending' && ` • ${currentLevelName}`}
          </span>
        </div>
        
        {/* Action buttons - ALL STACKED VERTICALLY */}
        {(showActions || isSuperAdmin) && tabType === 'pending' && approval.status === 'pending' && (
          <div 
            style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #E2E8F0', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="btn btn-success" style={{ width: '100%', padding: '0.75rem', fontSize: '0.9rem' }}
              onClick={() => initiateAction(approval.repair_id, 'approve')}>✓ Approve</button>
            <button className="btn btn-secondary" style={{ width: '100%', padding: '0.6rem', fontSize: '0.8rem' }}
              onClick={() => openAiChat(approval.repair_id)}>🤖 AI Analysis</button>
            <button className="btn" style={{ width: '100%', padding: '0.6rem', fontSize: '0.8rem', background: '#F59E0B', color: 'white' }}
              onClick={() => setInfoRequestModal({ show: true, repairId: approval.repair_id, reason: '' })}>❓ Request Info</button>
            <button className="btn btn-danger" style={{ width: '100%', padding: '0.6rem', fontSize: '0.8rem' }}
              onClick={() => initiateAction(approval.repair_id, 'reject')}>✗ Reject</button>
          </div>
        )}
        
        {/* Approved tab actions */}
        {tabType === 'approved' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #E2E8F0', width: '100%' }}
            onClick={(e) => e.stopPropagation()}>
            <button className="btn" style={{ width: '100%', padding: '0.75rem', fontSize: '0.9rem', background: '#8B5CF6', color: 'white' }}
              onClick={() => setWorkCompleteModal({ show: true, repairId: approval.repair_id, notes: '' })}>✓ Mark Work Complete</button>
            <button className="btn btn-secondary" style={{ width: '100%', padding: '0.6rem', fontSize: '0.8rem' }}
              onClick={() => setDetailModal({ show: true, approval })}>👁 View Details</button>
          </div>
        )}
        
        {/* Work Complete tab actions */}
        {tabType === 'work-complete' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #E2E8F0', width: '100%' }}
            onClick={(e) => e.stopPropagation()}>
            <button className="btn btn-primary" style={{ width: '100%', padding: '0.75rem', fontSize: '0.9rem' }}
              onClick={() => setInvoiceModal({ show: true, repair: approval, invoiceNumber: '', invoiceDate: new Date().toISOString().split('T')[0], finalAmount: approval.estimated_cost?.toString() || '', dueDate: '', workshopOutDate: new Date().toISOString().split('T')[0], invoiceFile: null })}>📄 Upload Invoice</button>
            <button className="btn btn-secondary" style={{ width: '100%', padding: '0.6rem', fontSize: '0.8rem' }}
              onClick={() => setDetailModal({ show: true, approval })}>👁 View Details</button>
          </div>
        )}
        
        {/* Info Required actions */}
        {tabType === 'info-required' && (
          <div style={{ marginTop: '0.5rem', width: '100%' }}>
            {approval.additional_info_requested && (
              <div style={{ fontSize: '0.75rem', color: '#92400E', padding: '0.5rem', background: '#FEF3C7', borderRadius: '4px', marginBottom: '0.5rem' }}>
                ❓ {approval.additional_info_requested.reason}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #E2E8F0', width: '100%' }}
              onClick={(e) => e.stopPropagation()}>
              <button className="btn btn-primary" style={{ width: '100%', padding: '0.75rem', fontSize: '0.9rem' }}
                onClick={() => setInfoResponseModal({ show: true, approval, response: '', files: [], fileCategory: 'quote' })}>📝 Respond to Request</button>
              <button className="btn btn-secondary" style={{ width: '100%', padding: '0.6rem', fontSize: '0.8rem' }}
                onClick={() => setDetailModal({ show: true, approval })}>👁 View Details</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render attachment with view tracking
  const renderAttachment = (attachment, repairId, index, vehicleNo = null) => {
    const isViewed = (viewedAttachments[repairId] || []).includes(attachment.id);
    const isImage = attachment.content_type?.startsWith('image/');
    const isPdf = attachment.content_type === 'application/pdf';
    const isMRReport = attachment.category === 'mr_report' || (attachment.filename && attachment.filename.includes('M&R Report'));
    // Get vehicleNo from attachment if not passed, or from filename
    const effectiveVehicleNo = vehicleNo || attachment.vehicle_no || (attachment.filename?.match(/XE\d+[A-Z]?/)?.[0]);
    
    const openPdfViewer = () => {
      recordAttachmentView(repairId, attachment.id);
      setPdfViewerModal({
        show: true,
        data: attachment.data,
        filename: attachment.filename,
        repairId: repairId,
        attachmentId: attachment.id
      });
    };
    
    return (
      <div 
        key={index}
        style={{
          padding: '0.75rem',
          background: isViewed ? '#D1FAE5' : '#F8FAFC',
          borderRadius: '4px',
          marginBottom: '0.5rem',
          border: isViewed ? '1px solid #10B981' : '1px solid #E2E8F0'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ marginRight: '0.5rem' }}>
              {attachment.category === 'quote' && '💰'}
              {attachment.category === 'damage_photo' && '📷'}
              {attachment.category === 'mr_report' && '📊'}
              {attachment.category === 'other' && '📁'}
              {!attachment.category && '📎'}
            </span>
            <strong>{attachment.filename}</strong>
            {isViewed && <span style={{ marginLeft: '0.5rem', color: '#10B981', fontSize: '0.75rem' }}>✓ Viewed</span>}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {isImage && attachment.data && (
              <button
                className="btn btn-secondary"
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                onClick={() => {
                  recordAttachmentView(repairId, attachment.id);
                  window.open(`data:${attachment.content_type};base64,${attachment.data}`, '_blank');
                }}
              >
                👁️ View
              </button>
            )}
            {isPdf && attachment.data && (
              isMRReport && effectiveVehicleNo ? (
                <>
                  <button
                    className="btn btn-primary"
                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                    onClick={async () => {
                      try {
                        setMessage({ type: 'info', text: 'Loading M&R Report...' });
                        const response = await fetch(`${BACKEND_URL}/api/mr/${effectiveVehicleNo}/generate-pdf`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (response.ok) {
                          const blob = await response.blob();
                          const blobUrl = URL.createObjectURL(blob);
                          window.open(blobUrl, '_blank');
                          setMessage(null);
                          recordAttachmentView(repairId, attachment.id);
                        } else {
                          const error = await response.json();
                          setMessage({ type: 'error', text: error.detail || 'Failed to load M&R' });
                          setTimeout(() => setMessage(null), 3000);
                        }
                      } catch (error) {
                        setMessage({ type: 'error', text: 'Failed to load M&R Report' });
                        setTimeout(() => setMessage(null), 3000);
                      }
                    }}
                  >
                    📄 View PDF
                  </button>
                </>
              ) : (
                <button
                  className="btn btn-primary"
                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                  onClick={openPdfViewer}
                >
                  📄 View PDF
                </button>
              )
            )}
            {attachment.data && (
              isMRReport && effectiveVehicleNo ? (
                <button
                  className="btn btn-secondary"
                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                  onClick={async () => {
                    try {
                      setMessage({ type: 'info', text: 'Downloading M&R Report...' });
                      const response = await fetch(`${BACKEND_URL}/api/mr/${effectiveVehicleNo}/generate-pdf`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (response.ok) {
                        const blob = await response.blob();
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `M&R Report - ${effectiveVehicleNo}.pdf`;
                        link.click();
                        URL.revokeObjectURL(url);
                        setMessage(null);
                        recordAttachmentView(repairId, attachment.id);
                      } else {
                        const error = await response.json();
                        setMessage({ type: 'error', text: error.detail || 'Failed to download' });
                        setTimeout(() => setMessage(null), 3000);
                      }
                    } catch (error) {
                      setMessage({ type: 'error', text: 'Failed to download M&R Report' });
                      setTimeout(() => setMessage(null), 3000);
                    }
                  }}
                >
                  ⬇️
                </button>
              ) : (
                <a
                  href={`data:${attachment.content_type || 'application/octet-stream'};base64,${attachment.data}`}
                  download={attachment.filename}
                  className="btn btn-secondary"
                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', textDecoration: 'none' }}
                  onClick={() => recordAttachmentView(repairId, attachment.id)}
                >
                  ⬇️
                </a>
              )
            )}
          </div>
        </div>
        {/* Show thumbnail for images */}
        {isImage && attachment.data && (
          <div style={{ marginTop: '0.5rem' }}>
            <img 
              src={`data:${attachment.content_type};base64,${attachment.data}`}
              alt={attachment.filename}
              style={{ maxWidth: '150px', maxHeight: '100px', borderRadius: '4px', cursor: 'pointer', border: '1px solid #E2E8F0' }}
              onClick={() => {
                recordAttachmentView(repairId, attachment.id);
                window.open(`data:${attachment.content_type};base64,${attachment.data}`, '_blank');
              }}
            />
          </div>
        )}
        {attachment.category === 'mr_report' && attachment.source === 'saved_report' && (
          <p style={{ fontSize: '0.7rem', color: '#64748B', marginTop: '0.25rem' }}>
            Generated: {new Date(attachment.generated_at).toLocaleDateString()}
          </p>
        )}
        {attachment.category === 'mr_report' && attachment.source === 'auto_converted' && (
          <p style={{ fontSize: '0.7rem', color: '#059669', marginTop: '0.25rem' }}>
            ✓ Auto-converted from {attachment.original_filename} • {attachment.record_count || 0} records
          </p>
        )}
      </div>
    );
  };

  const renderApprovalCard = (approval, showActions = false) => {
    const repair = approval.repair || {};
    const currentLevel = approval.current_level || 1;
    const requiredLevels = approval.required_levels || [];
    const attachments = repair.attachments || {};
    const allAttachments = repair.files || [];
    
    return (
      <div key={approval.repair_id} className="card" style={{ marginBottom: '1.5rem' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
          <div>
            <span className="vin-plate">{approval.vehicle_no}</span>
            <h3 style={{ marginTop: '0.5rem', fontSize: '1.25rem' }}>{repair.description}</h3>
            <p style={{ color: '#64748B', fontSize: '0.875rem' }}>
              Requested by {approval.requested_by} • {new Date(approval.requested_at).toLocaleDateString()}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span className={`badge ${getStatusBadge(approval.status)}`}>
              {approval.status?.toUpperCase()}
            </span>
            {approval.estimated_cost > 0 && (
              <p style={{ marginTop: '0.5rem', fontWeight: '600', fontSize: '1.1rem' }}>
                {formatCurrency(approval.estimated_cost)}
              </p>
            )}
          </div>
        </div>

        {/* Issue Details */}
        <div style={{ marginBottom: '1rem', padding: '1rem', background: '#F8FAFC', borderRadius: '4px' }}>
          <p><strong>Reported Issue:</strong></p>
          <p>{repair.reported_issue}</p>
          {repair.current_mileage && (
            <p style={{ marginTop: '0.5rem', color: '#64748B' }}>
              <strong>Current Mileage:</strong> {repair.current_mileage.toLocaleString()} km
            </p>
          )}
        </div>

        {/* Info Request History Summary */}
        <div style={{ marginBottom: '1rem', padding: '1rem', background: '#FFFBEB', borderRadius: '8px', border: '1px solid #FDE68A' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h4 style={{ margin: 0, color: '#92400E', fontSize: '0.9rem' }}>📋 Info Request History</h4>
            <button
              className="btn btn-secondary"
              style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
              onClick={() => setInfoHistoryModal({ show: true, repair })}
            >
              View Full History
            </button>
          </div>
          {(() => {
            const infoResponses = repair.info_responses || [];
            const pendingRequest = repair.additional_info_requested;
            const totalRequests = infoResponses.length + (pendingRequest ? 1 : 0);
            
            if (totalRequests === 0) {
              return (
                <p style={{ color: '#64748B', fontSize: '0.85rem', margin: 0 }}>
                  No info requests for this repair.
                </p>
              );
            }
            
            return (
              <div>
                <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  <strong>{totalRequests}</strong> info request(s) • <strong>{infoResponses.length}</strong> responded
                  {pendingRequest && <span style={{ color: '#DC2626', fontWeight: '600' }}> • 1 PENDING</span>}
                </p>
                {/* Show latest response or pending request */}
                {pendingRequest ? (
                  <div style={{ padding: '0.5rem', background: '#FEF2F2', borderRadius: '4px', fontSize: '0.8rem' }}>
                    <p style={{ color: '#DC2626', fontWeight: '600', margin: 0 }}>⏳ Awaiting Response:</p>
                    <p style={{ margin: '0.25rem 0 0 0', color: '#374151' }}>
                      "{pendingRequest.reason?.substring(0, 100)}{pendingRequest.reason?.length > 100 ? '...' : ''}"
                    </p>
                    <p style={{ margin: '0.25rem 0 0 0', color: '#64748B', fontSize: '0.75rem' }}>
                      Requested by {pendingRequest.requested_by} on {new Date(pendingRequest.requested_at).toLocaleDateString()}
                    </p>
                  </div>
                ) : infoResponses.length > 0 ? (
                  <div style={{ padding: '0.5rem', background: '#D1FAE5', borderRadius: '4px', fontSize: '0.8rem' }}>
                    <p style={{ color: '#059669', fontWeight: '600', margin: 0 }}>✓ Latest Response:</p>
                    <p style={{ margin: '0.25rem 0 0 0', color: '#374151' }}>
                      "{infoResponses[infoResponses.length - 1].response?.substring(0, 100)}{infoResponses[infoResponses.length - 1].response?.length > 100 ? '...' : ''}"
                    </p>
                    {infoResponses[infoResponses.length - 1].files_added > 0 && (
                      <p style={{ margin: '0.25rem 0 0 0', color: '#059669', fontSize: '0.75rem' }}>
                        📎 {infoResponses[infoResponses.length - 1].files_added} file(s) attached
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })()}
        </div>

        {/* Attachments Section */}
        {allAttachments.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h4>📎 Attachments ({allAttachments.length})</h4>
              {showActions && (
                <span style={{ fontSize: '0.75rem', color: hasViewedAttachments(approval.repair_id) ? '#10B981' : '#F59E0B' }}>
                  {hasViewedAttachments(approval.repair_id) 
                    ? `✓ ${(viewedAttachments[approval.repair_id] || []).length} viewed` 
                    : '⚠️ Not yet reviewed'}
                </span>
              )}
            </div>
            
            {/* Categorised attachments */}
            {attachments.mr_report && (
              <div style={{ marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <p style={{ fontSize: '0.75rem', fontWeight: '600', color: '#DC2626', margin: 0 }}>M&R Report:</p>
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '0.15rem 0.4rem', fontSize: '0.65rem' }}
                    onClick={async () => {
                      try {
                        setMessage({ type: 'info', text: 'Regenerating M&R PDF...' });
                        const response = await fetch(`${BACKEND_URL}/api/repairs/${approval.repair_id}/regenerate-mr-pdf`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (response.ok) {
                          const data = await response.json();
                          setMessage({ type: 'success', text: `M&R PDF regenerated: ${data.summary_count} suppliers, ${data.record_count} records` });
                          fetchData();
                        } else {
                          const error = await response.json();
                          setMessage({ type: 'error', text: error.detail || 'Failed to regenerate M&R PDF' });
                        }
                      } catch (error) {
                        setMessage({ type: 'error', text: 'Failed to regenerate M&R PDF' });
                      }
                      setTimeout(() => setMessage(null), 4000);
                    }}
                    title="Regenerate M&R PDF with latest data"
                  >
                    🔄 Regenerate
                  </button>
                </div>
                {renderAttachment({ ...attachments.mr_report, category: 'mr_report' }, approval.repair_id, 'mr', repair.vehicle_no)}
              </div>
            )}
            
            {attachments.quotes?.length > 0 && (
              <div style={{ marginBottom: '0.5rem' }}>
                <p style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748B', marginBottom: '0.25rem' }}>Quotes:</p>
                {attachments.quotes.map((att, idx) => renderAttachment({ ...att, category: 'quote' }, approval.repair_id, `quote-${idx}`))}
              </div>
            )}
            
            {attachments.damage_photos?.length > 0 && (
              <div style={{ marginBottom: '0.5rem' }}>
                <p style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748B', marginBottom: '0.25rem' }}>Damage Photos:</p>
                {attachments.damage_photos.map((att, idx) => renderAttachment({ ...att, category: 'damage_photo' }, approval.repair_id, `photo-${idx}`))}
              </div>
            )}
            
            {attachments.other?.length > 0 && (
              <div>
                <p style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748B', marginBottom: '0.25rem' }}>Other Documents:</p>
                {attachments.other.map((att, idx) => renderAttachment({ ...att, category: 'other' }, approval.repair_id, `other-${idx}`))}
              </div>
            )}
          </div>
        )}

        {/* Approval Progress */}
        {requiredLevels.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontWeight: '600', marginBottom: '0.75rem' }}>Approval Progress:</p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {requiredLevels.map((level, idx) => {
                const isCompleted = approval.approval_history?.some(h => h.level === level.level && h.action === 'approve');
                const isRejected = approval.approval_history?.some(h => h.level === level.level && h.action === 'reject');
                const isCurrent = currentLevel === level.level && approval.status === 'pending';
                
                return (
                  <React.Fragment key={level.level}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        background: isCompleted ? '#10B981' : isRejected ? '#EF4444' : isCurrent ? '#F97316' : '#E2E8F0',
                        color: isCompleted || isRejected || isCurrent ? 'white' : '#64748B'
                      }}>
                        {isCompleted ? '✓' : isRejected ? '✗' : level.level}
                      </div>
                      <span style={{ fontSize: '0.75rem', marginTop: '0.25rem', textAlign: 'center' }}>{level.name}</span>
                      <span style={{ fontSize: '0.65rem', color: '#64748B' }}>{formatCurrency(level.threshold_min)}+</span>
                    </div>
                    {idx < requiredLevels.length - 1 && (
                      <div style={{ flex: 0.5, height: '2px', background: isCompleted ? '#10B981' : '#E2E8F0', marginBottom: '2rem' }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Approval History */}
        {approval.approval_history?.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Approval History:</p>
            <div style={{ fontSize: '0.875rem' }}>
              {approval.approval_history.map((history, idx) => (
                <div key={idx} style={{ 
                  padding: '0.5rem', 
                  background: history.type === 'offline_approval_recorded' ? '#E0F2FE' : 
                             history.action === 'approve' ? '#D1FAE5' : '#FEE2E2',
                  borderRadius: '4px',
                  marginBottom: '0.25rem',
                  border: history.type === 'offline_approval_recorded' ? '1px solid #7DD3FC' : 'none'
                }}>
                  {history.type === 'offline_approval_recorded' ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1rem' }}>
                          {history.approval_method === 'whatsapp' ? '📱' : 
                           history.approval_method === 'email' ? '📧' : 
                           history.approval_method === 'phone' ? '📞' : 
                           history.approval_method === 'in_person' ? '🤝' : '💬'}
                        </span>
                        <strong style={{ color: '#0369A1' }}>Offline Approval:</strong>
                        <span>{history.approver_name}</span>
                        {history.has_screenshot && <span style={{ color: '#059669' }}>📷</span>}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748B', marginTop: '0.25rem' }}>
                        via {history.approval_method} on {history.approval_date} 
                        {' • '}Recorded by {history.recorded_by}
                      </div>
                      {history.remarks && (
                        <p style={{ fontSize: '0.8rem', fontStyle: 'italic', marginTop: '0.25rem' }}>"{history.remarks}"</p>
                      )}
                    </>
                  ) : (
                    <>
                      <strong>{history.role}</strong> {history.action === 'approve' ? 'approved' : 'rejected'} 
                      {' '}by {history.actioned_by} on {new Date(history.actioned_at).toLocaleDateString()}
                      {history.remarks && <span style={{ fontStyle: 'italic' }}> - "{history.remarks}"</span>}
                      {history.audit_note && (
                        <p style={{ fontSize: '0.7rem', color: '#DC2626', marginTop: '0.25rem' }}>⚠️ {history.audit_note}</p>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Analysis Summary */}
        {repair.analysis && (
          <div style={{ marginBottom: '1.5rem' }}>
            {/* Analysis Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', padding: '1rem', background: repair.analysis.approval_recommendation === 'APPROVE' ? '#DCFCE7' : repair.analysis.approval_recommendation === 'REJECT' ? '#FEE2E2' : '#FEF3C7', borderRadius: '8px', border: `2px solid ${repair.analysis.approval_recommendation === 'APPROVE' ? '#10B981' : repair.analysis.approval_recommendation === 'REJECT' ? '#EF4444' : '#F59E0B'}` }}>
              <div>
                <h4 style={{ margin: 0 }}>🤖 AI Analysis</h4>
                {repair.analysis.approval_reason && (
                  <p style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: '#374151' }}>{repair.analysis.approval_reason}</p>
                )}
              </div>
              {repair.analysis.approval_recommendation && (
                <span style={{ padding: '0.5rem 1.5rem', borderRadius: '20px', fontWeight: '700', fontSize: '1rem', background: repair.analysis.approval_recommendation === 'APPROVE' ? '#10B981' : repair.analysis.approval_recommendation === 'REJECT' ? '#EF4444' : '#F59E0B', color: 'white' }}>
                  {repair.analysis.approval_recommendation}
                </span>
              )}
            </div>
            
            {/* Business Impact ROI Section */}
            {repair.analysis.business_impact && (
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#EFF6FF', borderRadius: '8px', border: '1px solid #BFDBFE' }}>
                <h4 style={{ marginBottom: '0.75rem', color: '#1E40AF' }}>💰 Business Impact & ROI</h4>
                <p style={{ fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '500' }}>
                  {repair.analysis.business_impact.roi_summary}
                </p>
                {repair.analysis.business_impact.downtime_risk && (
                  <p style={{ fontSize: '0.85rem', color: repair.analysis.business_impact.downtime_risk === 'HIGH' ? '#DC2626' : repair.analysis.business_impact.downtime_risk === 'MEDIUM' ? '#F59E0B' : '#10B981' }}>
                    <strong>Downtime Risk:</strong> {repair.analysis.business_impact.downtime_risk}
                  </p>
                )}
                {repair.analysis.business_impact.repair_vs_replace && (
                  <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                    <strong>Recommendation:</strong> {repair.analysis.business_impact.repair_vs_replace}
                  </p>
                )}
              </div>
            )}

            {/* Downtime Analysis (calculated) */}
            {repair.analysis.downtime_analysis && (
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#F0FDF4', borderRadius: '8px', border: '1px solid #BBF7D0' }}>
                <h4 style={{ marginBottom: '0.75rem', color: '#166534' }}>📊 Downtime & Revenue Analysis</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#64748B' }}>Operational Period</p>
                    <p style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                      {repair.analysis.downtime_analysis.months_operational || 'N/A'} months
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#64748B' }}>Daily Income</p>
                    <p style={{ fontWeight: '600', fontSize: '1.1rem', fontFamily: 'JetBrains Mono' }}>
                      {formatCurrency(repair.analysis.downtime_analysis.daily_income || 500)}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#64748B' }}>Recovery Time</p>
                    <p style={{ fontWeight: '600', fontSize: '1.1rem', color: '#059669' }}>
                      {repair.analysis.downtime_analysis.days_to_recover_cost || 'N/A'} days
                    </p>
                  </div>
                </div>
                {repair.analysis.downtime_analysis.last_repair_date && (
                  <p style={{ fontSize: '0.8rem', color: '#64748B', marginTop: '0.75rem' }}>
                    Last repair: {repair.analysis.downtime_analysis.last_repair_date} ({repair.analysis.downtime_analysis.days_since_last_repair} days ago)
                  </p>
                )}
              </div>
            )}

            {/* Vehicle Lifecycle Assessment */}
            {repair.analysis.vehicle_lifecycle && repair.analysis.vehicle_lifecycle.age_years && (
              <div style={{ marginBottom: '1rem', padding: '1rem', background: repair.analysis.vehicle_lifecycle.approaching_replacement ? '#FEF2F2' : repair.analysis.vehicle_lifecycle.approaching_coe_expiry ? '#FFFBEB' : '#F8FAFC', borderRadius: '8px', border: `1px solid ${repair.analysis.vehicle_lifecycle.approaching_replacement ? '#FECACA' : repair.analysis.vehicle_lifecycle.approaching_coe_expiry ? '#FDE68A' : '#E2E8F0'}` }}>
                <h4 style={{ marginBottom: '0.75rem' }}>🚗 Vehicle Lifecycle</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#64748B' }}>Vehicle Age</p>
                    <p style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                      {repair.analysis.vehicle_lifecycle.age_years} years
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#64748B' }}>Replacement Threshold</p>
                    <p style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                      {repair.analysis.vehicle_lifecycle.replacement_threshold_years} years
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#64748B' }}>COE Max</p>
                    <p style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                      {repair.analysis.vehicle_lifecycle.coe_max_years} years
                    </p>
                  </div>
                </div>
                {repair.analysis.vehicle_lifecycle.approaching_replacement && (
                  <p style={{ color: '#DC2626', fontWeight: '600', marginTop: '0.75rem' }}>
                    ⚠️ Vehicle is approaching replacement threshold - consider replacement options
                  </p>
                )}
                {repair.analysis.vehicle_lifecycle.approaching_coe_expiry && !repair.analysis.vehicle_lifecycle.approaching_replacement && (
                  <p style={{ color: '#F59E0B', fontWeight: '600', marginTop: '0.75rem' }}>
                    ⚠️ Vehicle is approaching COE expiry - monitor repair costs closely
                  </p>
                )}
                {repair.analysis.vehicle_lifecycle_assessment && (
                  <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'white', borderRadius: '4px' }}>
                    <p style={{ fontSize: '0.85rem' }}>
                      <strong>Assessment:</strong> {repair.analysis.vehicle_lifecycle_assessment.lifecycle_notes}
                    </p>
                    {repair.analysis.vehicle_lifecycle_assessment.replacement_consideration && (
                      <span className={`badge ${repair.analysis.vehicle_lifecycle_assessment.replacement_consideration === 'CONTINUE' ? 'badge-approved' : repair.analysis.vehicle_lifecycle_assessment.replacement_consideration === 'CONSIDER_REPLACEMENT' ? 'badge-rejected' : 'badge-pending'}`} style={{ marginTop: '0.5rem' }}>
                        {repair.analysis.vehicle_lifecycle_assessment.replacement_consideration}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Part Lifespan Analysis */}
            {repair.analysis.part_lifespan && (
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#FDF4FF', borderRadius: '8px', border: '1px solid #E9D5FF' }}>
                <h4 style={{ marginBottom: '0.75rem', color: '#7C3AED' }}>🔧 Part Lifespan Analysis</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  {repair.analysis.part_lifespan.expected_lifespan_km > 0 && (
                    <div>
                      <p style={{ fontSize: '0.75rem', color: '#64748B' }}>Expected Lifespan</p>
                      <p style={{ fontWeight: '600' }}>
                        {repair.analysis.part_lifespan.expected_lifespan_km?.toLocaleString()} km / {repair.analysis.part_lifespan.expected_lifespan_years} years
                      </p>
                    </div>
                  )}
                  {repair.analysis.part_lifespan.typical_failure_causes && (
                    <div>
                      <p style={{ fontSize: '0.75rem', color: '#64748B' }}>Typical Failure Causes</p>
                      <p style={{ fontSize: '0.85rem' }}>{repair.analysis.part_lifespan.typical_failure_causes}</p>
                    </div>
                  )}
                </div>
                {repair.analysis.part_lifespan.maintenance_tips && (
                  <p style={{ fontSize: '0.85rem', marginTop: '0.75rem', color: '#6B7280' }}>
                    💡 <strong>Tip:</strong> {repair.analysis.part_lifespan.maintenance_tips}
                  </p>
                )}
              </div>
            )}

            {/* Service Analysis */}
            {repair.analysis.service_analysis && (
              <div style={{ marginBottom: '1rem', padding: '1rem', background: repair.analysis.service_analysis.service_status === 'OVERDUE' || repair.analysis.service_analysis.service_status === 'UNDER_SERVICED' ? '#FEF2F2' : '#F0FDF4', borderRadius: '8px' }}>
                <h4 style={{ marginBottom: '0.75rem' }}>🛠️ Service History Analysis</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span className={`badge ${repair.analysis.service_analysis.service_status === 'ON_SCHEDULE' ? 'badge-approved' : 'badge-rejected'}`}>
                    {repair.analysis.service_analysis.service_status?.replace('_', ' ')}
                  </span>
                  {repair.analysis.service_analysis.missed_services && (
                    <p style={{ fontSize: '0.85rem', color: '#DC2626' }}>{repair.analysis.service_analysis.missed_services}</p>
                  )}
                </div>
                {repair.analysis.service_analysis.service_recommendation && (
                  <p style={{ fontSize: '0.85rem', marginTop: '0.75rem' }}>
                    <strong>Recommendation:</strong> {repair.analysis.service_analysis.service_recommendation}
                  </p>
                )}
              </div>
            )}

            {/* Cost Comparison Table */}
            {repair.analysis.cost_comparison?.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '0.75rem' }}>💵 Cost Comparison</h4>
                <table className="table" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Current Quote</th>
                      <th>Historical Avg</th>
                      <th>Variance</th>
                      <th>Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {repair.analysis.cost_comparison.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.item}</td>
                        <td style={{ fontFamily: 'JetBrains Mono' }}>{formatCurrency(item.current_quote)}</td>
                        <td style={{ fontFamily: 'JetBrains Mono' }}>{formatCurrency(item.historical_average)}</td>
                        <td style={{ color: item.variance_percent > 20 ? '#DC2626' : item.variance_percent < -10 ? '#059669' : '#64748B', fontWeight: '600' }}>
                          {item.variance_percent > 0 ? '+' : ''}{item.variance_percent}%
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>{item.comment}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Cross-Fleet Vendor Rates */}
            {repair.analysis.cross_fleet_rates?.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '0.75rem' }}>🔍 Cross-Fleet Vendor Comparison</h4>
                <p style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '0.5rem' }}>
                  Rates for similar repairs across ALL vehicles in fleet
                </p>
                <table className="table" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Vendor</th>
                      <th>Jobs Done</th>
                      <th>Avg Cost</th>
                      <th>Total Spent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {repair.analysis.cross_fleet_rates.map((rate, idx) => (
                      <tr key={idx}>
                        <td><strong>{rate.vendor}</strong></td>
                        <td>{rate.jobs_done}</td>
                        <td style={{ fontFamily: 'JetBrains Mono', color: '#059669' }}>{formatCurrency(rate.avg_cost)}</td>
                        <td style={{ fontFamily: 'JetBrains Mono' }}>{formatCurrency(rate.total_spent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Recommendations */}
            {repair.analysis.recommendations?.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '0.75rem' }}>💡 Recommendations</h4>
                {repair.analysis.recommendations.map((rec, idx) => (
                  <div key={idx} style={{ padding: '0.75rem', background: rec.priority === 'HIGH' ? '#FEF2F2' : rec.priority === 'MEDIUM' ? '#FFFBEB' : '#F0FDF4', borderRadius: '4px', marginBottom: '0.5rem', borderLeft: `4px solid ${rec.priority === 'HIGH' ? '#DC2626' : rec.priority === 'MEDIUM' ? '#F59E0B' : '#10B981'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <strong>{rec.action}</strong>
                        <p style={{ fontSize: '0.85rem', color: '#64748B', marginTop: '0.25rem' }}>{rec.reason}</p>
                      </div>
                      <span className={`badge ${rec.priority === 'HIGH' ? 'badge-rejected' : rec.priority === 'MEDIUM' ? 'badge-pending' : 'badge-approved'}`}>
                        {rec.priority}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Key Insights */}
            {repair.analysis.insights?.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '0.75rem' }}>🔍 Key Insights</h4>
                <ul style={{ marginLeft: '1rem' }}>
                  {repair.analysis.insights.map((insight, idx) => (
                    <li key={idx} style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>{insight}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Frequent Repairs Alert */}
            {repair.analysis.frequent_repairs_alert?.length > 0 && (
              <div style={{ padding: '1rem', background: '#FEF2F2', borderRadius: '8px', border: '1px solid #FECACA', marginBottom: '1rem' }}>
                <strong style={{ color: '#DC2626' }}>⚠️ Frequent Repairs Alert:</strong>
                <ul style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                  {repair.analysis.frequent_repairs_alert.map((alert, idx) => (
                    <li key={idx} style={{ color: '#991B1B', fontSize: '0.9rem' }}>{alert}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Analysis Timestamp */}
            <p style={{ fontSize: '0.75rem', color: '#94A3B8', marginTop: '0.5rem' }}>
              Analysed at {new Date(repair.analysis.analyzed_at).toLocaleString()} by {repair.analysis.analyzed_by}
            </p>
          </div>
        )}

        {/* AI Chat Button */}
        {showActions && (
          <button
            className="btn btn-secondary"
            onClick={() => openAiChat(approval.repair_id)}
            style={{ marginBottom: '1rem' }}
          >
            💬 Ask AI Questions
          </button>
        )}

        {/* AI Conversations History */}
        {repair.ai_conversations?.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>💬 AI Q&A History ({repair.ai_conversations.length}):</p>
            <div style={{ maxHeight: '200px', overflow: 'auto', fontSize: '0.875rem' }}>
              {repair.ai_conversations.slice(-3).map((conv, idx) => (
                <div key={idx} style={{ padding: '0.5rem', background: '#F8FAFC', borderRadius: '4px', marginBottom: '0.5rem' }}>
                  <p style={{ fontWeight: '600', color: '#1E40AF' }}>Q: {conv.question}</p>
                  <p style={{ color: '#374151', marginTop: '0.25rem' }}>{conv.answer?.substring(0, 200)}...</p>
                  <p style={{ fontSize: '0.7rem', color: '#94A3B8' }}>{conv.asked_by} • {new Date(conv.asked_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        {showActions && approval.status === 'pending' && (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">Remarks (optional)</label>
              <textarea
                className="form-textarea"
                value={remarks[approval.repair_id] || ''}
                onChange={(e) => setRemarks({ ...remarks, [approval.repair_id]: e.target.value })}
                placeholder="Add notes or conditions for approval/rejection..."
                style={{ minHeight: '80px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <button
                className="btn btn-success"
                onClick={() => initiateAction(approval.repair_id, 'approve')}
              >
                ✓ Approve (Level {currentLevel})
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setInfoRequestModal({ show: true, repairId: approval.repair_id, reason: '' })}
              >
                📋 Request More Info
              </button>
              <button
                className="btn btn-danger"
                onClick={() => initiateAction(approval.repair_id, 'reject')}
              >
                ✗ Reject
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderSettingsTab = () => (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 className="card-title">Approval Thresholds Configuration</h3>
        {!editingConfig ? (
          <button className="btn btn-primary" onClick={() => setEditingConfig(true)}>
            Edit Configuration
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-success" onClick={saveApprovalConfig}>Save</button>
            <button className="btn btn-secondary" onClick={() => {
              setEditingConfig(false);
              setConfigLevels(approvalConfig?.levels || []);
            }}>Cancel</button>
          </div>
        )}
      </div>

      <p style={{ color: '#64748B', marginBottom: '1.5rem' }}>
        Configure approval levels based on estimated repair cost. Higher cost repairs require more approval levels.
      </p>

      <div style={{ marginBottom: '1.5rem' }}>
        {configLevels.map((level, idx) => (
          <div 
            key={idx} 
            style={{ 
              display: 'grid', 
              gridTemplateColumns: editingConfig ? '80px 1fr 1fr 150px 150px 80px' : '80px 1fr 1fr 150px 150px',
              gap: '1rem', 
              alignItems: 'center',
              padding: '1rem',
              background: idx % 2 === 0 ? '#F8FAFC' : 'white',
              borderRadius: '4px',
              marginBottom: '0.5rem'
            }}
          >
            <div>
              <span style={{ 
                background: '#F97316', 
                color: 'white', 
                borderRadius: '50%', 
                width: '32px', 
                height: '32px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontWeight: '600'
              }}>
                {level.level}
              </span>
            </div>
            
            {editingConfig ? (
              <>
                <input className="form-input" value={level.name} onChange={(e) => updateLevel(idx, 'name', e.target.value)} placeholder="Level Name" />
                <select className="form-select" value={level.role} onChange={(e) => updateLevel(idx, 'role', e.target.value)}>
                  <option value="team">Team Member</option>
                  <option value="assistant_manager">Assistant Manager</option>
                  <option value="manager">Manager</option>
                  <option value="senior_manager">Senior Manager</option>
                  <option value="finance">Finance</option>
                  <option value="ceo">CEO</option>
                </select>
                <input className="form-input" type="number" value={level.threshold_min} onChange={(e) => updateLevel(idx, 'threshold_min', parseFloat(e.target.value) || 0)} placeholder="Min $" />
                <input className="form-input" type="number" value={level.threshold_max === Infinity ? '' : level.threshold_max} onChange={(e) => updateLevel(idx, 'threshold_max', e.target.value ? parseFloat(e.target.value) : Infinity)} placeholder="Max $ (blank = unlimited)" />
                <button className="btn btn-danger" onClick={() => removeLevel(idx)} style={{ padding: '0.5rem' }}>✗</button>
              </>
            ) : (
              <>
                <div>
                  <strong>{level.name}</strong>
                  <span style={{ color: '#64748B', marginLeft: '0.5rem' }}>({level.role})</span>
                </div>
                <div></div>
                <div style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatCurrency(level.threshold_min)}</div>
                <div style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {level.threshold_max === Infinity || level.threshold_max === 'Infinity' ? '∞ (No limit)' : formatCurrency(level.threshold_max)}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {editingConfig && (
        <button className="btn btn-secondary" onClick={addLevel}>+ Add Approval Level</button>
      )}

      <div style={{ marginTop: '2rem', padding: '1rem', background: '#FEF3C7', borderRadius: '4px' }}>
        <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>ℹ️ How it works:</p>
        <ul style={{ marginLeft: '1rem', fontSize: '0.875rem', color: '#92400E' }}>
          <li>Repairs are routed through approval levels based on estimated cost</li>
          <li>Each level must approve before moving to the next</li>
          <li>Rejection at any level rejects the entire request</li>
          <li>Users see approvals pending at their designated level</li>
        </ul>
      </div>

      {/* Fleet Settings Section */}
      <div style={{ marginTop: '2rem', borderTop: '2px solid #E2E8F0', paddingTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 className="card-title">Fleet & Vehicle Settings</h3>
          {user?.is_super_admin && (
            !editingFleetSettings ? (
              <button className="btn btn-primary" onClick={() => setEditingFleetSettings(true)}>
                Edit Settings
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-success" onClick={saveFleetSettings}>Save</button>
                <button className="btn btn-secondary" onClick={() => {
                  setEditingFleetSettings(false);
                  fetchFleetSettings();
                }}>Cancel</button>
              </div>
            )
          )}
        </div>

        <p style={{ color: '#64748B', marginBottom: '1.5rem' }}>
          Configure vehicle lifecycle parameters and ROI calculation defaults.
        </p>

        {fleetSettings && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
            <div className="form-group">
              <label className="form-label">Vehicle Replacement Age (years)</label>
              {editingFleetSettings ? (
                <input
                  type="number"
                  className="form-input"
                  value={fleetSettings.vehicle_replacement_age_years || 15}
                  onChange={(e) => setFleetSettings({...fleetSettings, vehicle_replacement_age_years: parseInt(e.target.value) || 15})}
                />
              ) : (
                <p style={{ fontWeight: '600', fontSize: '1.25rem' }}>
                  {fleetSettings.vehicle_replacement_age_years || 15} years
                </p>
              )}
              <p style={{ fontSize: '0.75rem', color: '#64748B' }}>
                AI will flag vehicles approaching this age for replacement consideration
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">COE Maximum Age (years)</label>
              {editingFleetSettings ? (
                <input
                  type="number"
                  className="form-input"
                  value={fleetSettings.coe_max_years || 20}
                  onChange={(e) => setFleetSettings({...fleetSettings, coe_max_years: parseInt(e.target.value) || 20})}
                />
              ) : (
                <p style={{ fontWeight: '600', fontSize: '1.25rem' }}>
                  {fleetSettings.coe_max_years || 20} years
                </p>
              )}
              <p style={{ fontSize: '0.75rem', color: '#64748B' }}>
                Singapore COE maximum extension period
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Default Daily Income ($)</label>
              {editingFleetSettings ? (
                <input
                  type="number"
                  className="form-input"
                  value={fleetSettings.default_daily_income || 500}
                  onChange={(e) => setFleetSettings({...fleetSettings, default_daily_income: parseFloat(e.target.value) || 500})}
                />
              ) : (
                <p style={{ fontWeight: '600', fontSize: '1.25rem', fontFamily: 'JetBrains Mono' }}>
                  {formatCurrency(fleetSettings.default_daily_income || 500)}
                </p>
              )}
              <p style={{ fontSize: '0.75rem', color: '#64748B' }}>
                Used for ROI calculations when vehicle-specific income is not set
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Expected Service Interval (months)</label>
              {editingFleetSettings ? (
                <input
                  type="number"
                  className="form-input"
                  value={fleetSettings.maintenance_interval_months || 3}
                  onChange={(e) => setFleetSettings({...fleetSettings, maintenance_interval_months: parseInt(e.target.value) || 3})}
                />
              ) : (
                <p style={{ fontWeight: '600', fontSize: '1.25rem' }}>
                  {fleetSettings.maintenance_interval_months || 3} months
                </p>
              )}
              <p style={{ fontSize: '0.75rem', color: '#64748B' }}>
                AI will flag vehicles with overdue servicing
              </p>
            </div>
          </div>
        )}

        <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#EFF6FF', borderRadius: '4px' }}>
          <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>💡 Per-Vehicle Settings:</p>
          <p style={{ fontSize: '0.875rem', color: '#1E40AF' }}>
            You can override the default daily income for individual vehicles in the Vehicle details page.
            This allows for more accurate ROI calculations based on each vehicle's actual revenue contribution.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="approvals-page">
      <div className="page-header">
        <h2>Approval Queue</h2>
        <p>Review and manage repair approval requests</p>
      </div>

      {/* Toast Notification - Fixed position on top of everything */}
      {message && (
        <div 
          style={{ 
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: '400px',
            maxWidth: '600px',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            background: message.type === 'error' ? '#FEE2E2' : 
                       message.type === 'success' ? '#D1FAE5' : '#DBEAFE',
            border: `2px solid ${message.type === 'error' ? '#EF4444' : 
                                message.type === 'success' ? '#10B981' : '#3B82F6'}`,
            color: message.type === 'error' ? '#991B1B' : 
                   message.type === 'success' ? '#065F46' : '#1E40AF',
            fontWeight: '500',
            fontSize: '0.95rem'
          }}
        >
          <span style={{ fontSize: '1.5rem' }}>
            {message.type === 'error' ? '❌' : message.type === 'success' ? '✅' : 'ℹ️'}
          </span>
          <span style={{ flex: 1 }}>{message.text}</span>
          <button 
            onClick={() => setMessage(null)}
            style={{ 
              background: 'transparent', 
              border: 'none', 
              cursor: 'pointer',
              fontSize: '1.25rem',
              color: 'inherit',
              padding: '0.25rem'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setConfirmModal({ show: false, repairId: null, action: null, hasViewedAttachments: false });
            }
          }}
        >
          <div className="card" style={{ width: '500px', margin: 0 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1rem' }}>
              {confirmModal.action === 'approve' ? '✓ Confirm Approval' : '✗ Confirm Rejection'}
            </h3>
            
            {!confirmModal.hasViewedAttachments && (
              <div style={{ 
                padding: '1rem', 
                background: '#FEF2F2', 
                borderRadius: '4px', 
                marginBottom: '1rem',
                border: '1px solid #FECACA'
              }}>
                <p style={{ color: '#991B1B', fontWeight: '600' }}>⚠️ Attachments Not Reviewed</p>
                <p style={{ fontSize: '0.875rem', color: '#7F1D1D', marginTop: '0.5rem' }}>
                  You are about to {confirmModal.action} this request without reviewing the attachments. 
                  This will be recorded in the audit trail for compliance purposes.
                </p>
              </div>
            )}
            
            {confirmModal.hasViewedAttachments && (
              <div style={{ 
                padding: '1rem', 
                background: '#D1FAE5', 
                borderRadius: '4px', 
                marginBottom: '1rem',
                border: '1px solid #A7F3D0'
              }}>
                <p style={{ color: '#065F46' }}>
                  ✓ Attachments reviewed ({(viewedAttachments[confirmModal.repairId] || []).length} files)
                </p>
              </div>
            )}
            
            <p style={{ marginBottom: '1.5rem', color: '#64748B' }}>
              Are you sure you want to {confirmModal.action} this repair request?
            </p>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button 
                className={`btn ${confirmModal.action === 'approve' ? 'btn-success' : 'btn-danger'}`}
                onClick={handleAction}
              >
                Yes, {confirmModal.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setConfirmModal({ show: false, repairId: null, action: null, hasViewedAttachments: false })}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Chat Modal */}
      {aiChatModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100
        }}>
          <div className="card" style={{ width: '500px', maxWidth: '95vw', maxHeight: '70vh', display: 'flex', flexDirection: 'column', margin: 0, padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ fontSize: '1rem', margin: 0 }}>💬 Ask AI</h3>
              <button 
                className="btn btn-secondary"
                onClick={() => setAiChatModal({ show: false, repairId: null, conversations: [] })}
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
              >
                ✕
              </button>
            </div>
            
            {/* Question Input - Moved to top */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <input
                type="text"
                className="form-input"
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
                placeholder="Ask a question..."
                onKeyPress={(e) => e.key === 'Enter' && sendAiQuestion()}
                disabled={aiLoading}
                style={{ flex: 1, padding: '0.5rem', fontSize: '0.875rem' }}
                autoFocus
              />
              <button
                className="btn btn-primary"
                onClick={sendAiQuestion}
                disabled={aiLoading || !aiQuestion.trim()}
                style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
              >
                {aiLoading ? '...' : 'Send'}
              </button>
            </div>
            
            <p style={{ fontSize: '0.7rem', color: '#94A3B8', marginBottom: '0.5rem' }}>
              💡 "Is minimal repair ok?", "Repair history?", "Should we approve?"
            </p>
            
            {/* Conversation History */}
            <div style={{ 
              flex: 1, 
              overflow: 'auto', 
              padding: '0.75rem', 
              background: '#F8FAFC', 
              borderRadius: '4px',
              minHeight: '150px',
              maxHeight: '250px'
            }}>
              {aiChatModal.conversations.length === 0 && !aiLoading && (
                <p style={{ textAlign: 'center', color: '#94A3B8', padding: '1rem', fontSize: '0.875rem' }}>
                  No questions yet
                </p>
              )}
              
              {aiChatModal.conversations.map((conv, idx) => (
                <div key={idx} style={{ marginBottom: '1rem' }}>
                  {/* Question */}
                  <div style={{ 
                    background: '#DBEAFE', 
                    padding: '0.75rem', 
                    borderRadius: '8px 8px 8px 0',
                    marginBottom: '0.5rem'
                  }}>
                    <p style={{ fontWeight: '600', color: '#1E40AF' }}>{conv.question}</p>
                    <p style={{ fontSize: '0.7rem', color: '#64748B', marginTop: '0.25rem' }}>
                      {conv.asked_by} • {new Date(conv.asked_at).toLocaleString()}
                    </p>
                  </div>
                  
                  {/* Answer */}
                  {conv.answer ? (
                    <div style={{ 
                      background: 'white', 
                      padding: '0.75rem', 
                      borderRadius: '8px 8px 0 8px',
                      border: '1px solid #E2E8F0',
                      marginLeft: '1rem'
                    }}>
                      <p style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>{conv.answer}</p>
                    </div>
                  ) : (
                    <div style={{ marginLeft: '1rem', color: '#64748B' }}>
                      <div className="spinner" style={{ width: '20px', height: '20px', display: 'inline-block' }}></div>
                      <span style={{ marginLeft: '0.5rem' }}>AI is thinking...</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Request Additional Info Modal */}
      {infoRequestModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100
        }}>
          <div className="card" style={{ width: '500px', margin: 0 }}>
            <h3 style={{ marginBottom: '1rem' }}>📋 Request Additional Information</h3>
            <p style={{ color: '#64748B', marginBottom: '1rem' }}>
              Request the requester to provide additional quotes or information before you can make a decision.
            </p>
            
            <div className="form-group">
              <label className="form-label">What information is needed?</label>
              <textarea
                className="form-textarea"
                value={infoRequestModal.reason}
                onChange={(e) => setInfoRequestModal({ ...infoRequestModal, reason: e.target.value })}
                placeholder="e.g., Please provide a quote from another vendor for comparison, Need itemised breakdown of costs..."
                style={{ minHeight: '100px' }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button 
                className="btn btn-primary"
                onClick={requestAdditionalInfo}
                disabled={!infoRequestModal.reason.trim()}
              >
                Send Request
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setInfoRequestModal({ show: false, repairId: null, reason: '' })}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Response Modal - For responding to info requests */}
      {infoResponseModal.show && infoResponseModal.approval && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            overflow: 'auto',
            padding: '1rem'
          }}
          onClick={() => setInfoResponseModal({ show: false, approval: null, response: '', files: [], fileCategory: 'quote' })}
        >
          <div 
            className="card" 
            style={{ width: '550px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', padding: '1.5rem' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>📝 Respond to Information Request</h3>
            
            {/* Show the request */}
            <div style={{ 
              background: '#FEF3C7', 
              padding: '1rem', 
              borderRadius: '8px', 
              marginBottom: '1rem',
              border: '1px solid #F59E0B'
            }}>
              <p style={{ margin: 0, fontWeight: 600, color: '#B45309' }}>❓ Information Requested:</p>
              <p style={{ margin: '0.5rem 0 0 0', color: '#92400E' }}>
                {infoResponseModal.approval.info_request?.reason || 
                 infoResponseModal.approval.repair?.additional_info_requested?.reason ||
                 'Additional information required'}
              </p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.75rem', color: '#92400E' }}>
                Requested by: {(infoResponseModal.approval.info_request?.requested_by || 
                               infoResponseModal.approval.repair?.additional_info_requested?.requested_by || 
                               '').split('@')[0]}
              </p>
            </div>
            
            {/* Vehicle info */}
            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#F1F5F9', borderRadius: '4px' }}>
              <strong>Vehicle:</strong> {infoResponseModal.approval.vehicle_no} | 
              <strong> Cost:</strong> {formatCurrency(infoResponseModal.approval.estimated_cost)}
            </div>
            
            {/* Response input */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem' }}>
                Your Response:
              </label>
              <textarea
                value={infoResponseModal.response}
                onChange={(e) => setInfoResponseModal({ ...infoResponseModal, response: e.target.value })}
                placeholder="Provide the requested information here (optional if uploading files)..."
                style={{ 
                  width: '100%', 
                  minHeight: '80px', 
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: '1px solid #CBD5E1',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
            </div>
            
            {/* File upload section */}
            <div style={{ marginBottom: '1rem', padding: '1rem', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem' }}>
                📎 Upload Files (Quotes, Documents, Photos):
              </label>
              
              {/* File category selector */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '0.8rem', color: '#64748B', marginRight: '0.5rem' }}>File Category:</label>
                <select
                  value={infoResponseModal.fileCategory}
                  onChange={(e) => setInfoResponseModal({ ...infoResponseModal, fileCategory: e.target.value })}
                  style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #CBD5E1', fontSize: '0.85rem' }}
                >
                  <option value="quote">💰 Quote</option>
                  <option value="damage_photo">📷 Photo</option>
                  <option value="other">📄 Other Document</option>
                </select>
              </div>
              
              {/* File input */}
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                onChange={(e) => {
                  const newFiles = Array.from(e.target.files || []);
                  setInfoResponseModal({ 
                    ...infoResponseModal, 
                    files: [...(infoResponseModal.files || []), ...newFiles] 
                  });
                }}
                style={{ display: 'block', marginBottom: '0.5rem' }}
              />
              
              {/* Selected files list */}
              {(infoResponseModal.files?.length || 0) > 0 && (
                <div style={{ marginTop: '0.5rem' }}>
                  <p style={{ fontSize: '0.8rem', color: '#64748B', margin: '0 0 0.25rem 0' }}>Selected files:</p>
                  {infoResponseModal.files.map((file, idx) => (
                    <div key={idx} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      padding: '0.25rem 0.5rem',
                      background: '#E2E8F0',
                      borderRadius: '4px',
                      marginBottom: '0.25rem',
                      fontSize: '0.8rem'
                    }}>
                      <span>📄 {file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                      <button
                        onClick={() => {
                          const newFiles = (infoResponseModal.files || []).filter((_, i) => i !== idx);
                          setInfoResponseModal({ ...infoResponseModal, files: newFiles });
                        }}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer',
                          color: '#EF4444',
                          fontSize: '1rem'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <p style={{ fontSize: '0.75rem', color: '#64748B', margin: '0 0 1rem 0' }}>
              💡 After submitting, this repair will be automatically resubmitted for approval.
            </p>
            
            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button 
                className="btn btn-secondary"
                onClick={() => setInfoResponseModal({ show: false, approval: null, response: '', files: [], fileCategory: 'quote' })}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={submitInfoResponse}
                disabled={!infoResponseModal.response?.trim() && (infoResponseModal.files?.length || 0) === 0}
                style={{ opacity: (infoResponseModal.response?.trim() || (infoResponseModal.files?.length || 0) > 0) ? 1 : 0.5 }}
              >
                📤 Submit & Resubmit for Approval
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offline Approval Modal */}
      {offlineApprovalModal.show && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100
          }}
          onClick={() => setOfflineApprovalModal({ 
            show: false, 
            repairId: null,
            approverName: '',
            approvalMethod: 'whatsapp',
            approvalDate: new Date().toISOString().split('T')[0],
            remarks: '',
            screenshot: null
          })}
        >
          <div 
            className="card" 
            style={{ width: '400px', maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto', margin: 0, padding: '1rem' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>📱 Record Offline Approval</h3>
              <button 
                className="btn btn-secondary"
                onClick={() => setOfflineApprovalModal({ 
                  show: false, 
                  repairId: null,
                  approverName: '',
                  approvalMethod: 'whatsapp',
                  approvalDate: new Date().toISOString().split('T')[0],
                  remarks: '',
                  screenshot: null
                })}
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
              >
                ✕
              </button>
            </div>
            
            <div style={{ 
              padding: '0.5rem', 
              background: '#FEF3C7', 
              borderRadius: '4px', 
              marginBottom: '0.75rem',
              border: '1px solid #FCD34D'
            }}>
              <p style={{ fontSize: '0.75rem', color: '#92400E', margin: 0 }}>
                📋 Document approvals received via WhatsApp, phone, or email for audit purposes.
              </p>
            </div>
            
            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Approver Name *</label>
              <input
                type="text"
                className="form-input"
                value={offlineApprovalModal.approverName}
                onChange={(e) => setOfflineApprovalModal({ ...offlineApprovalModal, approverName: e.target.value })}
                placeholder="e.g., John Tan (CEO)"
                style={{ padding: '0.4rem 0.5rem', fontSize: '0.85rem' }}
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Method</label>
                <select
                  className="form-select"
                  value={offlineApprovalModal.approvalMethod}
                  onChange={(e) => setOfflineApprovalModal({ ...offlineApprovalModal, approvalMethod: e.target.value })}
                  style={{ padding: '0.4rem 0.5rem', fontSize: '0.85rem' }}
                >
                  <option value="whatsapp">📱 WhatsApp</option>
                  <option value="email">📧 Email</option>
                  <option value="phone">📞 Phone</option>
                  <option value="in_person">🤝 In Person</option>
                </select>
              </div>
              
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={offlineApprovalModal.approvalDate}
                  onChange={(e) => setOfflineApprovalModal({ ...offlineApprovalModal, approvalDate: e.target.value })}
                  style={{ padding: '0.4rem 0.5rem', fontSize: '0.85rem' }}
                />
              </div>
            </div>
            
            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Screenshot (Optional)</label>
              <div style={{ 
                border: '1px dashed #CBD5E1', 
                borderRadius: '4px', 
                padding: '0.5rem', 
                textAlign: 'center',
                background: offlineApprovalModal.screenshot ? '#D1FAE5' : '#F8FAFC',
                cursor: 'pointer'
              }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setOfflineApprovalModal({ ...offlineApprovalModal, screenshot: file });
                    }
                  }}
                  style={{ display: 'none' }}
                  id="offline-approval-screenshot"
                />
                <label htmlFor="offline-approval-screenshot" style={{ cursor: 'pointer', fontSize: '0.8rem' }}>
                  {offlineApprovalModal.screenshot ? (
                    <span style={{ color: '#059669' }}>✓ {offlineApprovalModal.screenshot.name}</span>
                  ) : (
                    <span style={{ color: '#64748B' }}>📷 Click to upload</span>
                  )}
                </label>
              </div>
            </div>
            
            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Remarks (Optional)</label>
              <textarea
                className="form-textarea"
                value={offlineApprovalModal.remarks}
                onChange={(e) => setOfflineApprovalModal({ ...offlineApprovalModal, remarks: e.target.value })}
                placeholder="Additional notes..."
                style={{ minHeight: '50px', fontSize: '0.85rem', padding: '0.4rem 0.5rem' }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className="btn"
                style={{ background: '#25D366', color: 'white', padding: '0.4rem 0.75rem', fontSize: '0.85rem' }}
                onClick={submitOfflineApproval}
                disabled={!offlineApprovalModal.approverName.trim()}
              >
                ✓ Record Approval
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setOfflineApprovalModal({ 
                  show: false, 
                  repairId: null,
                  approverName: '',
                  approvalMethod: 'whatsapp',
                  approvalDate: new Date().toISOString().split('T')[0],
                  remarks: '',
                  screenshot: null
                })}
                style={{ padding: '0.4rem 0.75rem', fontSize: '0.85rem' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs - Vertical stack on mobile */}
      <div 
        className="mobile-tabs-scroll"
        style={{ 
          display: isMobile ? 'grid' : 'flex',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : undefined,
          gap: isMobile ? '0.5rem' : '0.5rem', 
          marginBottom: '1rem',
          borderBottom: isMobile ? 'none' : '1px solid #E2E8F0',
          paddingBottom: '0.5rem',
          ...(!isMobile && { flexWrap: 'wrap' })
        }}
      >
        {/* Tab buttons */}
        {(() => {
          const mobileTabStyle = isMobile ? { 
            fontSize: '0.8rem', 
            padding: '0.75rem 0.5rem',
            width: '100%',
            textAlign: 'center',
            borderRadius: '8px',
            flexShrink: 0
          } : {};
          
          return (
            <>
              <button 
                className={`btn ${activeTab === 'pending' ? 'btn-primary' : 'btn-secondary'}`} 
                onClick={() => setActiveTab('pending')} 
                data-testid="tab-pending"
                style={mobileTabStyle}
              >
                🕐 Pending ({approvals.length})
              </button>
              <button 
                className={`btn ${activeTab === 'info-required' ? 'btn-primary' : 'btn-secondary'}`} 
                onClick={() => setActiveTab('info-required')} 
                data-testid="tab-info-required"
                style={{ ...mobileTabStyle, background: activeTab === 'info-required' ? '#F59E0B' : undefined }}
              >
                ❓ Info ({infoRequiredApprovals.length})
              </button>
              <button 
                className={`btn ${activeTab === 'approved' ? 'btn-primary' : 'btn-secondary'}`} 
                onClick={() => setActiveTab('approved')} 
                data-testid="tab-approved" 
                style={{ ...mobileTabStyle, background: activeTab === 'approved' ? '#10B981' : undefined }}
              >
                ✅ Approved ({approvedApprovals.length})
              </button>
              <button 
                className={`btn ${activeTab === 'work-complete' ? 'btn-primary' : 'btn-secondary'}`} 
                onClick={() => setActiveTab('work-complete')} 
                data-testid="tab-work-complete"
                style={{ ...mobileTabStyle, background: activeTab === 'work-complete' ? '#8B5CF6' : undefined }}
              >
                🔧 Work ({workCompleteApprovals.length})
              </button>
              <button 
                className={`btn ${activeTab === 'invoice-variance' ? 'btn-primary' : 'btn-secondary'}`} 
                onClick={() => setActiveTab('invoice-variance')} 
                data-testid="tab-invoice-variance"
                style={{ ...mobileTabStyle, background: activeTab === 'invoice-variance' ? '#F97316' : undefined }}
              >
                ⚠️ Variance ({invoiceVarianceApprovals.length})
              </button>
              <button 
                className={`btn ${activeTab === 'completed' ? 'btn-primary' : 'btn-secondary'}`} 
                onClick={() => setActiveTab('completed')} 
                data-testid="tab-completed"
                style={{ ...mobileTabStyle, background: activeTab === 'completed' ? '#059669' : undefined }}
              >
                ✓ Done ({completedJobs.length})
              </button>
              <button 
                className={`btn ${activeTab === 'rejected' ? 'btn-primary' : 'btn-secondary'}`} 
                onClick={() => setActiveTab('rejected')} 
                data-testid="tab-rejected" 
                style={{ ...mobileTabStyle, background: activeTab === 'rejected' ? '#EF4444' : undefined }}
              >
                ❌ Rejected ({rejectedApprovals.length})
              </button>
              <button 
                className={`btn ${activeTab === 'my-requests' ? 'btn-primary' : 'btn-secondary'}`} 
                onClick={() => setActiveTab('my-requests')} 
                data-testid="tab-my-requests"
                style={mobileTabStyle}
              >
                📤 Mine ({myRequests.length})
              </button>
              <button 
                className={`btn ${activeTab === 'all' ? 'btn-primary' : 'btn-secondary'}`} 
                onClick={() => setActiveTab('all')} 
                data-testid="tab-all"
                style={mobileTabStyle}
              >
                📋 All ({allApprovals.length})
              </button>
              {(['manager', 'ceo', 'admin'].includes(user?.role) || user?.is_super_admin) && (
                <button 
                  className={`btn ${activeTab === 'settings' ? 'btn-primary' : 'btn-secondary'}`} 
                  onClick={() => setActiveTab('settings')} 
                  data-testid="tab-settings"
                  style={mobileTabStyle}
                >
                  ⚙️ Settings
                </button>
              )}
              <button 
                className={`btn ${showFilters ? 'btn-primary' : 'btn-secondary'}`} 
                onClick={() => setShowFilters(!showFilters)}
                style={{ ...mobileTabStyle, marginLeft: isMobile ? 0 : 'auto' }}
              >
                🔍 {isMobile ? '' : 'Filters '}{Object.values(filters).some(f => f) && '•'}
              </button>
            </>
          );
        })()}
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="card" style={{ marginBottom: '1rem', padding: '1rem', background: '#F8FAFC' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: '0.8rem' }}>Vehicle No</label>
              <input 
                className="form-input"
                placeholder="Search vehicle..."
                value={filters.vehicleNo}
                onChange={(e) => setFilters({...filters, vehicleNo: e.target.value})}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: '0.8rem' }}>Date From</label>
              <input 
                type="date"
                className="form-input"
                value={filters.dateFrom}
                onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: '0.8rem' }}>Date To</label>
              <input 
                type="date"
                className="form-input"
                value={filters.dateTo}
                onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: '0.8rem' }}>Approver</label>
              <input 
                className="form-input"
                placeholder="Search approver..."
                value={filters.approver}
                onChange={(e) => setFilters({...filters, approver: e.target.value})}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: '0.8rem' }}>Cost Min ($)</label>
              <input 
                type="number"
                className="form-input"
                placeholder="0"
                value={filters.costMin}
                onChange={(e) => setFilters({...filters, costMin: e.target.value})}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: '0.8rem' }}>Cost Max ($)</label>
              <input 
                type="number"
                className="form-input"
                placeholder="999999"
                value={filters.costMax}
                onChange={(e) => setFilters({...filters, costMax: e.target.value})}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'end' }}>
              <button className="btn btn-secondary" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="spinner"></div>}

      {/* Pending Approvals Tab */}
      {activeTab === 'pending' && !loading && (
        <>
          {applyFilters(approvals).length === 0 ? (
            <div className="card" data-testid="no-approvals">
              <p style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>
                ✅ No pending approvals. All caught up!
              </p>
            </div>
          ) : isMobile ? (
            // Mobile: Card layout
            <div data-testid="approval-list">
              {applyFilters(approvals).map(approval => renderMobileApprovalCard(approval, true))}
              <p style={{ fontSize: '0.75rem', color: '#64748B', textAlign: 'center', padding: '0.5rem' }}>
                💡 Tap any card to view full details
              </p>
            </div>
          ) : (
            // Desktop: Table layout
            <div className="card" style={{ padding: 0, overflow: 'hidden' }} data-testid="approval-list">
              <table className="table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Description</th>
                    <th>Cost</th>
                    <th>Submitted</th>
                    <th style={{ color: '#DC2626' }}>⏰ Pending</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applyFilters(approvals).map(approval => renderApprovalRow(approval, true))}
                </tbody>
              </table>
              <p style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#64748B', background: '#F8FAFC', margin: 0 }}>
                💡 Click any row to view full details
              </p>
            </div>
          )}
        </>
      )}

      {/* My Requests Tab */}
      {activeTab === 'my-requests' && !loading && (
        <>
          {applyFilters(myRequests).length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>
                📋 You have not submitted any approval requests yet.
              </p>
            </div>
          ) : isMobile ? (
            // Mobile: Card layout
            <div data-testid="my-requests-list">
              {applyFilters(myRequests).map(approval => renderMobileApprovalCard(approval, false))}
            </div>
          ) : (
            // Desktop: Table layout
            <div className="card" style={{ padding: 0, overflow: 'hidden' }} data-testid="my-requests-list">
              <table className="table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Description</th>
                    <th>Cost</th>
                    <th>Submitted</th>
                    <th style={{ color: '#DC2626' }}>⏰ Pending</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applyFilters(myRequests).map(approval => renderApprovalRow(approval, false))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* All Approvals Tab */}
      {activeTab === 'all' && !loading && (
        <>
          {applyFilters(allApprovals).length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>
                📋 No approval records found.
              </p>
            </div>
          ) : isMobile ? (
            // Mobile: Card layout
            <div data-testid="all-approvals-list">
              {applyFilters(allApprovals).map(approval => renderMobileApprovalCard(approval, isSuperAdmin))}
            </div>
          ) : (
            // Desktop: Table layout
            <div className="card" style={{ padding: 0, overflow: 'hidden' }} data-testid="all-approvals-list">
              <table className="table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Description</th>
                    <th>Cost</th>
                    <th>Submitted</th>
                    <th style={{ color: '#DC2626' }}>⏰ Pending</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applyFilters(allApprovals).map(approval => renderApprovalRow(approval, isSuperAdmin))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Approved Tab */}
      {activeTab === 'approved' && !loading && (
        <>
          {applyFilters(approvedApprovals).length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>
                ✅ No approved repairs found.
              </p>
            </div>
          ) : isMobile ? (
            // Mobile: Card layout
            <div data-testid="approved-list">
              {applyFilters(approvedApprovals).map(approval => renderMobileApprovalCard(approval, false, 'approved'))}
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }} data-testid="approved-list">
              <table className="table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Description</th>
                    <th>Cost</th>
                    <th>Submitted</th>
                    <th>Approved</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applyFilters(approvedApprovals).map(approval => (
                    <tr 
                      key={approval.repair_id}
                      onClick={() => setDetailModal({ show: true, approval })}
                      style={{ cursor: 'pointer' }}
                      className="approval-row"
                    >
                      <td><span className="vin-plate">{approval.vehicle_no}</span></td>
                      <td>{approval.repair?.description || 'N/A'}</td>
                      <td style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatCurrency(approval.estimated_cost)}</td>
                      <td>{new Date(approval.requested_at).toLocaleDateString()}</td>
                      <td style={{ color: '#10B981' }}>
                        {approval.completed_at ? new Date(approval.completed_at).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <button 
                            className="btn btn-secondary"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                            onClick={(e) => { e.stopPropagation(); setDetailModal({ show: true, approval }); }}
                          >
                            View
                          </button>
                          <button 
                            className="btn"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: '#8B5CF6', color: 'white' }}
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setWorkCompleteModal({ show: true, repairId: approval.repair_id, notes: '' }); 
                            }}
                          >
                            ✓ Work Done
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#10B981', background: '#DCFCE7', margin: 0 }}>
                ✅ These repairs have been approved and are ready for work/invoice processing
              </p>
            </div>
          )}
        </>
      )}

      {/* Work Complete Tab - Awaiting Invoice */}
      {activeTab === 'work-complete' && !loading && (
        <>
          {workCompleteApprovals.length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>
                🔧 No repairs awaiting invoice upload.
              </p>
            </div>
          ) : isMobile ? (
            // Mobile: Card layout
            <div data-testid="work-complete-list">
              {workCompleteApprovals.map(approval => renderMobileApprovalCard(approval, false, 'work-complete'))}
              <p style={{ fontSize: '0.75rem', color: '#8B5CF6', textAlign: 'center', padding: '0.5rem' }}>
                🔧 Upload invoice to close these jobs
              </p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }} data-testid="work-complete-list">
              <table className="table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Description</th>
                    <th>Approved Cost</th>
                    <th>Work Completed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {workCompleteApprovals.map(approval => (
                    <tr 
                      key={approval.repair_id}
                      onClick={() => setDetailModal({ show: true, approval })}
                      style={{ cursor: 'pointer', background: '#F5F3FF' }}
                      className="approval-row"
                    >
                      <td><span className="vin-plate">{approval.vehicle_no}</span></td>
                      <td>{approval.repair?.description || 'N/A'}</td>
                      <td style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatCurrency(approval.estimated_cost)}</td>
                      <td style={{ color: '#8B5CF6' }}>
                        {approval.repair?.work_completed_at ? new Date(approval.repair.work_completed_at).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <button 
                            className="btn"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: '#8B5CF6', color: 'white' }}
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setInvoiceModal({ 
                                show: true, 
                                repair: approval,
                                invoiceNumber: '',
                                invoiceDate: new Date().toISOString().split('T')[0],
                                finalAmount: approval.estimated_cost?.toString() || '',
                                dueDate: '',
                                invoiceFile: null
                              }); 
                            }}
                          >
                            📄 Upload Invoice
                          </button>
                          <button 
                            className="btn btn-secondary"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setSupplementaryModal({ 
                                show: true, 
                                repairId: approval.repair_id,
                                description: '',
                                estimatedCost: '',
                                quoteFiles: []
                              }); 
                            }}
                          >
                            ➕ Add Supplementary
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#8B5CF6', background: '#F5F3FF', margin: 0 }}>
                🔧 Work completed - Upload final invoice to close the job
              </p>
            </div>
          )}
        </>
      )}

      {/* Invoice Variance Tab */}
      {activeTab === 'invoice-variance' && !loading && (
        <>
          {invoiceVarianceApprovals.length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>
                ✓ No invoices with variance pending resolution.
              </p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }} data-testid="variance-list">
              <table className="table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Description</th>
                    <th>Approved</th>
                    <th>Invoice Amount</th>
                    <th>Variance</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceVarianceApprovals.map(approval => {
                    const invoice = approval.repair?.invoice || {};
                    const variance = invoice.variance || 0;
                    const varianceStatus = invoice.variance_status || 'pending';
                    
                    return (
                      <tr 
                        key={approval.repair_id}
                        onClick={() => setDetailModal({ show: true, approval })}
                        style={{ cursor: 'pointer', background: variance > 0 ? '#FEF3C7' : '#DCFCE7' }}
                        className="approval-row"
                      >
                        <td><span className="vin-plate">{approval.vehicle_no}</span></td>
                        <td>{approval.repair?.description || 'N/A'}</td>
                        <td style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatCurrency(approval.estimated_cost)}</td>
                        <td style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatCurrency(invoice.final_amount)}</td>
                        <td style={{ 
                          fontFamily: 'JetBrains Mono, monospace',
                          color: variance > 0 ? '#DC2626' : '#059669',
                          fontWeight: 'bold'
                        }}>
                          {variance >= 0 ? '+' : ''}{formatCurrency(variance)}
                          <span style={{ fontSize: '0.7rem', marginLeft: '0.25rem' }}>
                            ({invoice.variance_percent?.toFixed(1)}%)
                          </span>
                        </td>
                        <td>
                          <span style={{ 
                            padding: '0.15rem 0.4rem', 
                            borderRadius: '4px', 
                            fontSize: '0.7rem',
                            background: varianceStatus === 'explanation_provided' ? '#DCFCE7' : 
                                       Math.abs(variance) > 100 ? '#FEE2E2' : '#FEF3C7',
                            color: varianceStatus === 'explanation_provided' ? '#059669' : 
                                  Math.abs(variance) > 100 ? '#DC2626' : '#B45309'
                          }}>
                            {varianceStatus === 'explanation_provided' ? '📝 Explained' :
                             Math.abs(variance) > 100 ? '🔴 Needs Approval' : '⚠️ Needs Explanation'}
                          </span>
                        </td>
                        <td>
                          <button 
                            className="btn"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: '#F97316', color: 'white' }}
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setVarianceModal({ 
                                show: true, 
                                repair: approval,
                                explanation: invoice.variance_explanation || '',
                                action: Math.abs(variance) > 100 ? 'approve' : 'explain'
                              }); 
                            }}
                          >
                            {varianceStatus === 'explanation_provided' ? '✓ Close Job' : '⚠️ Resolve'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#F97316', background: '#FFF7ED', margin: 0 }}>
                ⚠️ Invoice variance requires resolution before job can be closed
              </p>
            </div>
          )}
        </>
      )}

      {/* Completed Jobs Tab */}
      {activeTab === 'completed' && !loading && (
        <>
          {completedJobs.length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>
                📁 No completed jobs found.
              </p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }} data-testid="completed-list">
              <table className="table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Description</th>
                    <th>Quote</th>
                    <th>Invoice</th>
                    <th>Variance</th>
                    <th>Completed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {completedJobs.map(approval => {
                    const invoice = approval.repair?.invoice || {};
                    const variance = invoice.variance || 0;
                    
                    return (
                      <tr 
                        key={approval.repair_id}
                        onClick={() => setDetailModal({ show: true, approval })}
                        style={{ cursor: 'pointer' }}
                        className="approval-row"
                      >
                        <td><span className="vin-plate">{approval.vehicle_no}</span></td>
                        <td>
                          {approval.repair?.description || 'N/A'}
                          {approval.repair?.is_supplementary && (
                            <span style={{ 
                              marginLeft: '0.5rem', 
                              padding: '0.1rem 0.3rem', 
                              background: '#E0E7FF', 
                              color: '#4338CA',
                              borderRadius: '3px',
                              fontSize: '0.65rem'
                            }}>
                              SUPP
                            </span>
                          )}
                        </td>
                        <td style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatCurrency(approval.estimated_cost)}</td>
                        <td style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatCurrency(invoice.final_amount)}</td>
                        <td style={{ 
                          fontFamily: 'JetBrains Mono, monospace',
                          color: variance > 0 ? '#DC2626' : variance < 0 ? '#059669' : '#64748B',
                          fontSize: '0.8rem'
                        }}>
                          {variance !== 0 ? (variance >= 0 ? '+' : '') + formatCurrency(variance) : '-'}
                        </td>
                        <td style={{ color: '#059669' }}>
                          {approval.repair?.completed_at ? new Date(approval.repair.completed_at).toLocaleDateString() : '-'}
                        </td>
                        <td>
                          <button 
                            className="btn btn-secondary"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                            onClick={(e) => { e.stopPropagation(); setDetailModal({ show: true, approval }); }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#059669', background: '#DCFCE7', margin: 0 }}>
                ✓ Completed jobs - Invoice processed and ready for export
              </p>
            </div>
          )}
        </>
      )}

      {/* Rejected Tab (Archive) */}
      {activeTab === 'rejected' && !loading && (
        <>
          {applyFilters(rejectedApprovals).length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>
                📁 No rejected repairs in archive.
              </p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }} data-testid="rejected-list">
              <table className="table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Description</th>
                    <th>Cost</th>
                    <th>Submitted</th>
                    <th>Rejected</th>
                    <th>Reason</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applyFilters(rejectedApprovals).map(approval => (
                    <tr 
                      key={approval.repair_id}
                      onClick={() => setDetailModal({ show: true, approval })}
                      style={{ cursor: 'pointer', background: '#FEF2F2' }}
                      className="approval-row"
                    >
                      <td><span className="vin-plate">{approval.vehicle_no}</span></td>
                      <td>{approval.repair?.description || 'N/A'}</td>
                      <td style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatCurrency(approval.estimated_cost)}</td>
                      <td>{new Date(approval.requested_at).toLocaleDateString()}</td>
                      <td style={{ color: '#DC2626' }}>
                        {approval.completed_at ? new Date(approval.completed_at).toLocaleDateString() : '-'}
                      </td>
                      <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {approval.rejection_reason || approval.approval_history?.[approval.approval_history.length - 1]?.remarks || '-'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <button 
                            className="btn btn-secondary"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                            onClick={(e) => { e.stopPropagation(); setDetailModal({ show: true, approval }); }}
                          >
                            View
                          </button>
                          <button 
                            className="btn"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: '#8B5CF6', color: 'white' }}
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setWorkCompleteModal({ show: true, repairId: approval.repair_id, notes: '' }); 
                            }}
                          >
                            ✓ Work Done
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#DC2626', background: '#FEE2E2', margin: 0 }}>
                ❌ Archived rejected repairs - these require resubmission if repair is still needed
              </p>
            </div>
          )}
        </>
      )}

      {/* Info Required Tab */}
      {activeTab === 'info-required' && !loading && (
        <>
          {infoRequiredApprovals.length === 0 ? (
            <div className="card">
              <p style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>
                ✓ No repairs awaiting additional information.
              </p>
            </div>
          ) : isMobile ? (
            // Mobile: Card layout
            <div data-testid="info-required-list">
              {infoRequiredApprovals.map(approval => renderMobileApprovalCard(approval, false, 'info-required'))}
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }} data-testid="info-required-list">
              <table className="table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Description</th>
                    <th>Cost</th>
                    <th>Info Requested</th>
                    <th>Requested By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {infoRequiredApprovals.map(approval => {
                    const infoRequest = approval.info_request || approval.repair?.additional_info_requested || {};
                    return (
                      <tr 
                        key={approval.repair_id}
                        style={{ cursor: 'pointer', background: '#FEF3C7' }}
                        className="approval-row"
                      >
                        <td><span className="vin-plate">{approval.vehicle_no}</span></td>
                        <td>{approval.repair?.description || 'N/A'}</td>
                        <td style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatCurrency(approval.estimated_cost)}</td>
                        <td style={{ maxWidth: '250px' }}>
                          <div style={{ 
                            background: '#FEF3C7', 
                            padding: '0.5rem', 
                            borderRadius: '4px',
                            border: '1px solid #F59E0B'
                          }}>
                            <strong style={{ color: '#B45309' }}>❓ {infoRequest.reason || 'Additional information required'}</strong>
                            <p style={{ fontSize: '0.75rem', color: '#92400E', margin: '0.25rem 0 0 0' }}>
                              {infoRequest.requested_at ? new Date(infoRequest.requested_at).toLocaleDateString() : ''}
                            </p>
                          </div>
                        </td>
                        <td style={{ fontSize: '0.85rem', color: '#64748B' }}>
                          {infoRequest.requested_by?.split('@')[0] || '-'}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button 
                              className="btn btn-primary"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setInfoResponseModal({ show: true, approval, response: '', files: [], fileCategory: 'quote' });
                              }}
                            >
                              📝 Respond
                            </button>
                            <button 
                              className="btn btn-secondary"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                              onClick={(e) => { e.stopPropagation(); setDetailModal({ show: true, approval }); }}
                            >
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#B45309', background: '#FEF3C7', margin: 0 }}>
                ❓ These repairs require additional information before approval can proceed. Click "Respond" to provide information and resubmit.
              </p>
            </div>
          )}
        </>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && !loading && renderSettingsTab()}

      {/* Detail Modal - Shows full approval details */}
      {detailModal.show && detailModal.approval && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'center',
            zIndex: 1000,
            overflow: 'auto',
            padding: isMobile ? '0.5rem' : '1rem',
            paddingTop: isMobile ? '60px' : '1rem'
          }}
          onClick={(e) => {
            // Close modal when clicking outside (on the overlay)
            if (e.target === e.currentTarget) {
              setDetailModal({ show: false, approval: null });
            }
          }}
        >
          <div className="card" style={{ 
            width: isMobile ? '100%' : '700px', 
            maxWidth: isMobile ? '100%' : '95vw', 
            maxHeight: isMobile ? 'calc(100vh - 80px)' : '90vh', 
            overflow: 'auto', 
            margin: 0,
            borderRadius: isMobile ? '12px' : undefined
          }} onClick={(e) => e.stopPropagation()}>
            {(() => {
              const approval = detailModal.approval;
              const repair = approval.repair || {};
              const daysPending = getDaysPending(approval.requested_at);
              const urgencyColor = getUrgencyColor(daysPending);
              const allAttachments = repair.files || [];
              const attachments = repair.attachments || {};
              const currentLevel = approval.current_level || 1;
              const requiredLevels = approval.required_levels || [];
              
              return (
                <>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                    <div>
                      <span className="vin-plate">{approval.vehicle_no}</span>
                      <h3 style={{ marginTop: '0.5rem', fontSize: '1.1rem', marginBottom: '0.25rem' }}>{repair.description}</h3>
                      <p style={{ color: '#64748B', fontSize: '0.8rem', margin: 0 }}>
                        Requested by {approval.requested_by}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button 
                        className="btn"
                        onClick={async () => {
                          try {
                            const response = await fetch(`${BACKEND_URL}/api/repairs/${approval.repair_id}/export-whatsapp`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (response.ok) {
                              const data = await response.json();
                              const link = document.createElement('a');
                              link.href = `data:application/pdf;base64,${data.pdf}`;
                              link.download = data.filename;
                              link.click();
                            }
                          } catch (e) { console.error('Export error:', e); }
                        }}
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem', background: '#25D366', color: 'white', border: 'none' }}
                        title="Export PDF for WhatsApp"
                      >
                        📱 Export
                      </button>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => setDetailModal({ show: false, approval: null })}
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  {/* Key Metrics */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={{ background: '#F8FAFC', padding: '0.75rem', borderRadius: '4px', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>{formatCurrency(approval.estimated_cost)}</div>
                      <div style={{ fontSize: '0.7rem', color: '#64748B' }}>Estimated Cost</div>
                    </div>
                    <div style={{ background: '#F8FAFC', padding: '0.75rem', borderRadius: '4px', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.8rem', color: '#64748B' }}>
                        {new Date(approval.requested_at).toLocaleDateString()}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#64748B' }}>Date Submitted</div>
                    </div>
                    <div style={{ background: urgencyColor === '#DC2626' ? '#FEE2E2' : urgencyColor === '#F59E0B' ? '#FEF3C7' : '#F8FAFC', padding: '0.75rem', borderRadius: '4px', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: '700', color: urgencyColor }}>
                        {daysPending} day{daysPending !== 1 ? 's' : ''}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: urgencyColor }}>⏰ PENDING</div>
                    </div>
                  </div>

                  {/* Issue Details */}
                  <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#F8FAFC', borderRadius: '4px' }}>
                    <p style={{ fontWeight: '600', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Reported Issue:</p>
                    <p style={{ fontSize: '0.875rem', margin: 0 }}>{repair.reported_issue || 'N/A'}</p>
                    {repair.current_mileage && (
                      <p style={{ marginTop: '0.5rem', color: '#64748B', fontSize: '0.8rem', margin: 0 }}>
                        Mileage: {repair.current_mileage.toLocaleString()} km
                      </p>
                    )}
                  </div>

                  {/* Info Request History Summary */}
                  <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#FFFBEB', borderRadius: '8px', border: '1px solid #FDE68A' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: '600', color: '#92400E', fontSize: '0.85rem' }}>📋 Info Request History</span>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem' }}
                        onClick={() => setInfoHistoryModal({ show: true, repair })}
                      >
                        View Full History
                      </button>
                    </div>
                    {(() => {
                      const infoResponses = repair.info_responses || [];
                      const pendingRequest = repair.additional_info_requested;
                      const totalRequests = infoResponses.length + (pendingRequest ? 1 : 0);
                      
                      if (totalRequests === 0) {
                        return (
                          <p style={{ color: '#64748B', fontSize: '0.8rem', margin: 0 }}>
                            No info requests for this repair.
                          </p>
                        );
                      }
                      
                      return (
                        <div>
                          <p style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                            <strong>{totalRequests}</strong> request(s) • <strong>{infoResponses.length}</strong> responded
                            {pendingRequest && <span style={{ color: '#DC2626', fontWeight: '600' }}> • 1 PENDING</span>}
                          </p>
                          {pendingRequest ? (
                            <p style={{ fontSize: '0.75rem', color: '#DC2626', margin: 0 }}>
                              ⏳ Awaiting: "{pendingRequest.reason?.substring(0, 60)}..."
                            </p>
                          ) : infoResponses.length > 0 ? (
                            <p style={{ fontSize: '0.75rem', color: '#059669', margin: 0 }}>
                              ✓ Last response: "{infoResponses[infoResponses.length - 1].response?.substring(0, 60)}..."
                            </p>
                          ) : null}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Approval Progress */}
                  {requiredLevels.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <p style={{ fontWeight: '600', fontSize: '0.8rem', marginBottom: '0.5rem' }}>Approval Progress:</p>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        {requiredLevels.map((level, idx) => {
                          const isCompleted = approval.approval_history?.some(h => h.level === level.level && h.action === 'approve');
                          const isRejected = approval.approval_history?.some(h => h.level === level.level && h.action === 'reject');
                          const isCurrent = currentLevel === level.level && approval.status === 'pending';
                          
                          return (
                            <React.Fragment key={level.level}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: '600',
                                  fontSize: '0.75rem',
                                  background: isCompleted ? '#10B981' : isRejected ? '#EF4444' : isCurrent ? '#F97316' : '#E2E8F0',
                                  color: isCompleted || isRejected || isCurrent ? 'white' : '#64748B'
                                }}>
                                  {isCompleted ? '✓' : isRejected ? '✗' : level.level}
                                </div>
                                <span style={{ fontSize: '0.65rem', marginTop: '0.25rem', textAlign: 'center' }}>{level.name}</span>
                              </div>
                              {idx < requiredLevels.length - 1 && (
                                <div style={{ flex: 0.3, height: '2px', background: isCompleted ? '#10B981' : '#E2E8F0', marginBottom: '1.5rem' }} />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* AI Analysis Summary - COMPREHENSIVE VIEW */}
                  {repair.analysis && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      {/* Analysis Header */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', padding: '1rem', background: repair.analysis.approval_recommendation === 'APPROVE' ? '#DCFCE7' : repair.analysis.approval_recommendation === 'REJECT' ? '#FEE2E2' : '#FEF3C7', borderRadius: '8px', border: `2px solid ${repair.analysis.approval_recommendation === 'APPROVE' ? '#10B981' : repair.analysis.approval_recommendation === 'REJECT' ? '#EF4444' : '#F59E0B'}` }}>
                        <div>
                          <h4 style={{ margin: 0 }}>🤖 AI Analysis</h4>
                          {repair.analysis.approval_reason && (
                            <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: '#374151' }}>{repair.analysis.approval_reason}</p>
                          )}
                        </div>
                        {repair.analysis.approval_recommendation && (
                          <span style={{ padding: '0.5rem 1.5rem', borderRadius: '20px', fontWeight: '700', fontSize: '1rem', background: repair.analysis.approval_recommendation === 'APPROVE' ? '#10B981' : repair.analysis.approval_recommendation === 'REJECT' ? '#EF4444' : '#F59E0B', color: 'white' }}>
                            {repair.analysis.approval_recommendation}
                          </span>
                        )}
                      </div>
                      
                      {/* Business Impact ROI Section */}
                      {repair.analysis.business_impact && (
                        <div style={{ marginBottom: '1rem', padding: '1rem', background: '#EFF6FF', borderRadius: '8px', border: '1px solid #BFDBFE' }}>
                          <h4 style={{ marginBottom: '0.75rem', color: '#1E40AF', fontSize: '0.9rem' }}>💰 Business Impact & ROI</h4>
                          <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem', fontWeight: '500' }}>
                            {repair.analysis.business_impact.roi_summary}
                          </p>
                          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                            {repair.analysis.business_impact.downtime_risk && (
                              <span style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem', borderRadius: '4px', background: repair.analysis.business_impact.downtime_risk === 'HIGH' ? '#FEE2E2' : repair.analysis.business_impact.downtime_risk === 'MEDIUM' ? '#FEF3C7' : '#DCFCE7', color: repair.analysis.business_impact.downtime_risk === 'HIGH' ? '#DC2626' : repair.analysis.business_impact.downtime_risk === 'MEDIUM' ? '#D97706' : '#059669' }}>
                                Downtime Risk: {repair.analysis.business_impact.downtime_risk}
                              </span>
                            )}
                          </div>
                          {repair.analysis.business_impact.repair_vs_replace && (
                            <p style={{ fontSize: '0.8rem', marginTop: '0.5rem', color: '#1E40AF' }}>
                              <strong>Strategy:</strong> {repair.analysis.business_impact.repair_vs_replace}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Downtime Analysis Grid */}
                      {repair.analysis.downtime_analysis && (
                        <div style={{ marginBottom: '1rem', padding: '1rem', background: '#F0FDF4', borderRadius: '8px', border: '1px solid #BBF7D0' }}>
                          <h4 style={{ marginBottom: '0.75rem', color: '#166534', fontSize: '0.9rem' }}>📊 Revenue & Recovery</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                            <div>
                              <p style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.25rem' }}>Daily Income</p>
                              <p style={{ fontWeight: '600', fontSize: '1rem', fontFamily: 'JetBrains Mono, monospace' }}>
                                {formatCurrency(repair.analysis.downtime_analysis.daily_income || 500)}
                              </p>
                            </div>
                            <div>
                              <p style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.25rem' }}>Recovery Time</p>
                              <p style={{ fontWeight: '600', fontSize: '1rem', color: '#059669' }}>
                                {repair.analysis.downtime_analysis.days_to_recover_cost || 'N/A'} days
                              </p>
                            </div>
                            <div>
                              <p style={{ fontSize: '0.7rem', color: '#64748B', marginBottom: '0.25rem' }}>Operational Period</p>
                              <p style={{ fontWeight: '600', fontSize: '1rem' }}>
                                {repair.analysis.downtime_analysis.months_operational || 'N/A'} months
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Vehicle Lifecycle */}
                      {repair.analysis.vehicle_lifecycle_assessment && (
                        <div style={{ marginBottom: '1rem', padding: '1rem', background: repair.analysis.vehicle_lifecycle_assessment.replacement_consideration === 'CONSIDER_REPLACEMENT' ? '#FEF2F2' : '#F8FAFC', borderRadius: '8px' }}>
                          <h4 style={{ marginBottom: '0.75rem', fontSize: '0.9rem' }}>🚗 Vehicle Lifecycle</h4>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            <span className={`badge ${repair.analysis.vehicle_lifecycle_assessment.replacement_consideration === 'CONTINUE' ? 'badge-approved' : repair.analysis.vehicle_lifecycle_assessment.replacement_consideration === 'CONSIDER_REPLACEMENT' ? 'badge-rejected' : 'badge-pending'}`}>
                              {repair.analysis.vehicle_lifecycle_assessment.replacement_consideration}
                            </span>
                            {repair.analysis.vehicle_lifecycle_assessment.years_remaining > 0 && (
                              <span style={{ fontSize: '0.8rem', color: '#64748B' }}>
                                ~{repair.analysis.vehicle_lifecycle_assessment.years_remaining} years remaining
                              </span>
                            )}
                          </div>
                          {repair.analysis.vehicle_lifecycle_assessment.lifecycle_notes && (
                            <p style={{ fontSize: '0.8rem', color: '#374151' }}>{repair.analysis.vehicle_lifecycle_assessment.lifecycle_notes}</p>
                          )}
                        </div>
                      )}

                      {/* Part Lifespan */}
                      {repair.analysis.part_lifespan && repair.analysis.part_lifespan.expected_lifespan_km > 0 && (
                        <div style={{ marginBottom: '1rem', padding: '1rem', background: '#FDF4FF', borderRadius: '8px', border: '1px solid #E9D5FF' }}>
                          <h4 style={{ marginBottom: '0.5rem', color: '#7C3AED', fontSize: '0.9rem' }}>🔧 Part Lifespan</h4>
                          <p style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                            <strong>Expected:</strong> {repair.analysis.part_lifespan.expected_lifespan_km?.toLocaleString()} km / {repair.analysis.part_lifespan.expected_lifespan_years} years
                          </p>
                          {repair.analysis.part_lifespan.maintenance_tips && (
                            <p style={{ fontSize: '0.8rem', color: '#6B7280', marginTop: '0.5rem' }}>
                              💡 {repair.analysis.part_lifespan.maintenance_tips}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Service Analysis Alert */}
                      {repair.analysis.service_analysis && repair.analysis.service_analysis.service_status && repair.analysis.service_analysis.service_status !== 'ON_SCHEDULE' && (
                        <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#FEF2F2', borderRadius: '8px', border: '1px solid #FECACA' }}>
                          <span className="badge badge-rejected" style={{ marginRight: '0.5rem' }}>
                            ⚠️ {repair.analysis.service_analysis.service_status}
                          </span>
                          {repair.analysis.service_analysis.missed_services && (
                            <span style={{ fontSize: '0.8rem', color: '#991B1B' }}>{repair.analysis.service_analysis.missed_services}</span>
                          )}
                        </div>
                      )}

                      {/* Cross-Fleet Vendor Rates */}
                      {repair.analysis.cross_fleet_rates && repair.analysis.cross_fleet_rates.length > 0 && (
                        <div style={{ marginBottom: '1rem' }}>
                          <h4 style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>🔍 Cross-Fleet Vendor Rates</h4>
                          <table className="table" style={{ fontSize: '0.8rem' }}>
                            <thead>
                              <tr>
                                <th>Vendor</th>
                                <th>Jobs</th>
                                <th>Avg Cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {repair.analysis.cross_fleet_rates.slice(0, 5).map((rate, idx) => (
                                <tr key={idx}>
                                  <td><strong>{rate.vendor}</strong></td>
                                  <td>{rate.jobs_done}</td>
                                  <td style={{ fontFamily: 'JetBrains Mono, monospace', color: '#059669' }}>{formatCurrency(rate.avg_cost)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Recommendations */}
                      {repair.analysis.recommendations && repair.analysis.recommendations.length > 0 && (
                        <div style={{ marginBottom: '1rem' }}>
                          <h4 style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>💡 Recommendations</h4>
                          {repair.analysis.recommendations.map((rec, idx) => (
                            <div key={idx} style={{ padding: '0.5rem', background: rec.priority === 'HIGH' ? '#FEF2F2' : rec.priority === 'MEDIUM' ? '#FFFBEB' : '#F0FDF4', borderRadius: '4px', marginBottom: '0.5rem', borderLeft: `3px solid ${rec.priority === 'HIGH' ? '#DC2626' : rec.priority === 'MEDIUM' ? '#F59E0B' : '#10B981'}` }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                <div style={{ flex: 1 }}>
                                  <strong style={{ fontSize: '0.85rem' }}>{rec.action}</strong>
                                  <p style={{ fontSize: '0.75rem', color: '#64748B', margin: '0.25rem 0 0 0' }}>{rec.reason}</p>
                                </div>
                                <span className={`badge ${rec.priority === 'HIGH' ? 'badge-rejected' : rec.priority === 'MEDIUM' ? 'badge-pending' : 'badge-approved'}`} style={{ marginLeft: '0.5rem', fontSize: '0.65rem' }}>
                                  {rec.priority}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Key Insights */}
                      {repair.analysis.insights && repair.analysis.insights.length > 0 && (
                        <div style={{ marginBottom: '1rem' }}>
                          <h4 style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>🔍 Key Insights</h4>
                          <ul style={{ marginLeft: '1rem', fontSize: '0.8rem' }}>
                            {repair.analysis.insights.map((insight, idx) => (
                              <li key={idx} style={{ marginBottom: '0.25rem' }}>{insight}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Analysis Timestamp */}
                      <p style={{ fontSize: '0.7rem', color: '#94A3B8', marginTop: '0.5rem' }}>
                        Analysed {new Date(repair.analysis.analyzed_at).toLocaleString()}
                      </p>
                    </div>
                  )}

                  {/* Attachments */}
                  {allAttachments.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <p style={{ fontWeight: '600', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                        📎 Attachments ({allAttachments.length})
                      </p>
                      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                        {allAttachments.map((att, idx) => {
                          const isImage = att.content_type?.startsWith('image/');
                          const isPDF = att.content_type === 'application/pdf';
                          
                          return (
                            <div key={idx} style={{ padding: '0.5rem', background: '#F8FAFC', borderRadius: '4px', marginBottom: '0.5rem' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.8rem' }}>
                                  {att.category === 'quote' && '💰'}
                                  {att.category === 'damage_photo' && '📷'}
                                  {(att.category === 'mr_report' || att.filename?.includes('M&R Report')) && '📊'}
                                  {att.category === 'quote' && '💰'}
                                  {att.category === 'damage_photo' && '📷'}
                                  {att.category === 'other' && '📁'}
                                  {!att.category && !att.filename?.includes('M&R Report') && '📎'}
                                  {' '}{att.filename}
                                </span>
                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                  {(att.category === 'mr_report' || att.filename?.includes('M&R Report')) && (
                                    <button
                                      className="btn btn-secondary"
                                      style={{ padding: '0.2rem 0.4rem', fontSize: '0.65rem' }}
                                      onClick={async () => {
                                        try {
                                          setMessage({ type: 'info', text: 'Regenerating M&R PDF...' });
                                          const response = await fetch(`${BACKEND_URL}/api/repairs/${approval.repair_id}/regenerate-mr-pdf`, {
                                            method: 'POST',
                                            headers: { 'Authorization': `Bearer ${token}` }
                                          });
                                          if (response.ok) {
                                            const data = await response.json();
                                            setMessage({ type: 'success', text: `M&R regenerated: ${data.summary_count} suppliers, ${data.record_count} records` });
                                            fetchData();
                                            setDetailModal({ show: false, approval: null });
                                          } else {
                                            const error = await response.json();
                                            setMessage({ type: 'error', text: error.detail || 'Failed to regenerate' });
                                          }
                                        } catch (error) {
                                          setMessage({ type: 'error', text: 'Failed to regenerate M&R PDF' });
                                        }
                                        setTimeout(() => setMessage(null), 4000);
                                      }}
                                      title="Regenerate M&R PDF with latest data"
                                    >
                                      🔄
                                    </button>
                                  )}
                                  {att.data && (
                                    <>
                                      {(att.category === 'mr_report' || att.filename?.includes('M&R Report')) ? (
                                        <>
                                          <button
                                            className="btn btn-primary"
                                            style={{ padding: '0.2rem 0.4rem', fontSize: '0.7rem' }}
                                            onClick={async () => {
                                              try {
                                                setMessage({ type: 'info', text: 'Loading M&R Report...' });
                                                const response = await fetch(`${BACKEND_URL}/api/mr/${repair.vehicle_no}/generate-pdf`, {
                                                  headers: { 'Authorization': `Bearer ${token}` }
                                                });
                                                if (response.ok) {
                                                  const blob = await response.blob();
                                                  const blobUrl = URL.createObjectURL(blob);
                                                  window.open(blobUrl, '_blank');
                                                  setMessage(null);
                                                } else {
                                                  const error = await response.json();
                                                  setMessage({ type: 'error', text: error.detail || 'Failed to load M&R Report' });
                                                  setTimeout(() => setMessage(null), 3000);
                                                }
                                              } catch (error) {
                                                setMessage({ type: 'error', text: 'Failed to load M&R Report' });
                                                setTimeout(() => setMessage(null), 3000);
                                              }
                                            }}
                                          >
                                            📄 View
                                          </button>
                                          <button
                                            className="btn btn-secondary"
                                            style={{ padding: '0.2rem 0.4rem', fontSize: '0.7rem' }}
                                            onClick={async () => {
                                              try {
                                                setMessage({ type: 'info', text: 'Downloading M&R Report...' });
                                                const response = await fetch(`${BACKEND_URL}/api/mr/${repair.vehicle_no}/generate-pdf`, {
                                                  headers: { 'Authorization': `Bearer ${token}` }
                                                });
                                                if (response.ok) {
                                                  const blob = await response.blob();
                                                  const url = URL.createObjectURL(blob);
                                                  const link = document.createElement('a');
                                                  link.href = url;
                                                  link.download = `M&R Report - ${repair.vehicle_no}.pdf`;
                                                  link.click();
                                                  URL.revokeObjectURL(url);
                                                  setMessage(null);
                                                } else {
                                                  const error = await response.json();
                                                  setMessage({ type: 'error', text: error.detail || 'Failed to download' });
                                                  setTimeout(() => setMessage(null), 3000);
                                                }
                                              } catch (error) {
                                                setMessage({ type: 'error', text: 'Failed to download M&R Report' });
                                                setTimeout(() => setMessage(null), 3000);
                                              }
                                            }}
                                          >
                                            ⬇️
                                          </button>
                                        </>
                                      ) : isPDF ? (
                                        <button
                                          className="btn btn-primary"
                                          style={{ padding: '0.2rem 0.4rem', fontSize: '0.7rem' }}
                                          onClick={() => {
                                            setPdfViewerModal({
                                              show: true,
                                              data: att.data,
                                              filename: att.filename,
                                              repairId: approval.repair_id,
                                              attachmentId: att.id
                                            });
                                          }}
                                        >
                                          📄 View
                                        </button>
                                      ) : (
                                        <button
                                          className="btn btn-secondary"
                                          style={{ padding: '0.2rem 0.4rem', fontSize: '0.7rem' }}
                                          onClick={() => {
                                            const dataUrl = `data:${att.content_type || 'application/octet-stream'};base64,${att.data}`;
                                            if (isImage) {
                                              const win = window.open('', '_blank');
                                              win.document.write(`<html><head><title>${att.filename}</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#1a1a1a;"><img src="${dataUrl}" style="max-width:100%;max-height:100vh;object-fit:contain;"/></body></html>`);
                                            } else {
                                              window.open(dataUrl, '_blank');
                                            }
                                          }}
                                        >
                                          👁️ View
                                        </button>
                                      )}
                                      {att.category !== 'mr_report' && !att.filename?.includes('M&R Report') && (
                                        <button
                                          className="btn btn-secondary"
                                          style={{ padding: '0.2rem 0.4rem', fontSize: '0.7rem' }}
                                          onClick={() => {
                                            const link = document.createElement('a');
                                            link.href = `data:${att.content_type || 'application/octet-stream'};base64,${att.data}`;
                                            link.download = att.filename;
                                            link.click();
                                          }}
                                        >
                                          ⬇️
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                              {/* Preview for images */}
                              {isImage && att.data && (
                                <img 
                                  src={`data:${att.content_type};base64,${att.data}`}
                                  alt={att.filename}
                                  style={{ marginTop: '0.5rem', maxWidth: '100%', maxHeight: '120px', objectFit: 'contain', borderRadius: '4px', cursor: 'pointer' }}
                                  onClick={() => {
                                    const win = window.open('', '_blank');
                                    win.document.write(`<html><head><title>${att.filename}</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#1a1a1a;"><img src="data:${att.content_type};base64,${att.data}" style="max-width:100%;max-height:100vh;object-fit:contain;"/></body></html>`);
                                  }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Extracted Quote Data */}
                  {repair.extracted_quotes && repair.extracted_quotes.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <p style={{ fontWeight: '600', fontSize: '0.8rem', marginBottom: '0.5rem' }}>💰 Extracted Quote Data</p>
                      <table className="table" style={{ fontSize: '0.75rem' }}>
                        <thead>
                          <tr>
                            <th>Supplier</th>
                            <th>Total</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {repair.extracted_quotes.map((q, idx) => (
                            <tr key={idx}>
                              <td>{q.data?.supplier || 'Unknown'}</td>
                              <td style={{ fontFamily: 'JetBrains Mono' }}>${(q.data?.total || 0).toFixed(2)}</td>
                              <td>{q.data?.quote_date || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Recorded Offline Approvals */}
                  {repair.offline_approvals && repair.offline_approvals.length > 0 && (
                    <div style={{ 
                      marginBottom: '1rem', 
                      padding: '0.75rem', 
                      background: '#E0F2FE', 
                      borderRadius: '8px',
                      border: '1px solid #7DD3FC'
                    }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#0369A1', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        📱 Offline Approvals Recorded ({repair.offline_approvals.length})
                      </h4>
                      {repair.offline_approvals.map((oa, idx) => (
                        <div key={idx} style={{ 
                          background: 'white', 
                          padding: '0.5rem 0.75rem', 
                          borderRadius: '4px',
                          marginBottom: idx < repair.offline_approvals.length - 1 ? '0.5rem' : 0,
                          border: '1px solid #BAE6FD'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{ fontSize: '1rem' }}>
                                {oa.approval_method === 'whatsapp' ? '📱' : 
                                 oa.approval_method === 'email' ? '📧' : 
                                 oa.approval_method === 'phone' ? '📞' : 
                                 oa.approval_method === 'in_person' ? '🤝' : '💬'}
                              </span>
                              <strong style={{ fontSize: '0.85rem' }}>{oa.approver_name}</strong>
                              {oa.screenshot && <span style={{ color: '#059669', fontSize: '0.8rem' }}>📷</span>}
                            </div>
                            <span style={{ fontSize: '0.75rem', color: '#64748B' }}>
                              {oa.approval_date}
                            </span>
                          </div>
                          {oa.remarks && (
                            <p style={{ fontSize: '0.8rem', color: '#64748B', margin: '0.25rem 0 0 1.5rem', fontStyle: 'italic' }}>
                              "{oa.remarks}"
                            </p>
                          )}
                          <p style={{ fontSize: '0.7rem', color: '#94A3B8', margin: '0.25rem 0 0 1.5rem' }}>
                            Recorded by {oa.recorded_by} on {new Date(oa.recorded_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  {(approval.status === 'pending' && (isSuperAdmin || activeTab === 'pending')) && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingTop: '0.75rem', borderTop: '1px solid #E2E8F0' }}>
                      <button 
                        className="btn btn-success"
                        onClick={() => { initiateAction(approval.repair_id, 'approve'); setDetailModal({ show: false, approval: null }); }}
                      >
                        ✓ Approve
                      </button>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => { openAiChat(approval.repair_id); }}
                      >
                        🤖 Ask AI
                      </button>
                      <button 
                        className="btn"
                        style={{ background: '#25D366', color: 'white' }}
                        onClick={() => { 
                          setOfflineApprovalModal({ 
                            show: true, 
                            repairId: approval.repair_id,
                            approverName: '',
                            approvalMethod: 'whatsapp',
                            approvalDate: new Date().toISOString().split('T')[0],
                            remarks: '',
                            screenshot: null
                          }); 
                        }}
                        title="Record approval received via WhatsApp/Email/Phone"
                      >
                        📱 Record Offline Approval
                      </button>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => { setInfoRequestModal({ show: true, repairId: approval.repair_id, reason: '' }); }}
                      >
                        📋 Request Info
                      </button>
                      <button 
                        className="btn btn-danger"
                        onClick={() => { initiateAction(approval.repair_id, 'reject'); setDetailModal({ show: false, approval: null }); }}
                      >
                        ✗ Reject
                      </button>
                      {isSuperAdmin && (
                        <button 
                          className="btn"
                          style={{ background: '#6B7280', color: 'white' }}
                          onClick={() => deleteApproval(approval.repair_id)}
                        >
                          🗑️ Delete Approval
                        </button>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {pdfViewerModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{ 
            width: '90vw', 
            height: '90vh', 
            background: 'white', 
            borderRadius: '8px', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '1rem', 
              borderBottom: '1px solid #E2E8F0',
              background: '#F8FAFC'
            }}>
              <h3 style={{ margin: 0 }}>📄 {pdfViewerModal.filename}</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <a
                  href={`data:application/pdf;base64,${pdfViewerModal.data}`}
                  download={pdfViewerModal.filename}
                  className="btn btn-secondary"
                  style={{ padding: '0.5rem 1rem', textDecoration: 'none' }}
                >
                  ⬇️ Download
                </a>
                <button 
                  className="btn btn-primary"
                  onClick={() => setPdfViewerModal({ show: false, data: null, filename: '', repairId: null, attachmentId: null })}
                  style={{ padding: '0.5rem 1rem' }}
                >
                  ✕ Close
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <object
                data={`data:application/pdf;base64,${pdfViewerModal.data}`}
                type="application/pdf"
                style={{ width: '100%', height: '100%', border: 'none' }}
              >
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <p style={{ marginBottom: '1rem', color: '#64748B' }}>
                    PDF preview not available in this browser. Please download to view.
                  </p>
                  <a
                    href={`data:application/pdf;base64,${pdfViewerModal.data}`}
                    download={pdfViewerModal.filename}
                    className="btn btn-primary"
                  >
                    ⬇️ Download PDF
                  </a>
                  <button
                    className="btn btn-secondary"
                    style={{ marginLeft: '0.5rem' }}
                    onClick={() => {
                      // Open in new tab as blob URL
                      const byteCharacters = atob(pdfViewerModal.data);
                      const byteNumbers = new Array(byteCharacters.length);
                      for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                      }
                      const byteArray = new Uint8Array(byteNumbers);
                      const blob = new Blob([byteArray], { type: 'application/pdf' });
                      const blobUrl = URL.createObjectURL(blob);
                      window.open(blobUrl, '_blank');
                    }}
                  >
                    🔗 Open in New Tab
                  </button>
                </div>
              </object>
            </div>
          </div>
        </div>
      )}

      {/* Info History Modal */}
      {infoHistoryModal.show && infoHistoryModal.repair && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setInfoHistoryModal({ show: false, repair: null });
            }
          }}
        >
          <div 
            className="card" 
            style={{ 
              maxWidth: '700px', 
              width: '95vw',
              maxHeight: '85vh', 
              overflow: 'hidden', 
              display: 'flex', 
              flexDirection: 'column',
              margin: 0,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', borderBottom: '1px solid #E2E8F0', paddingBottom: '1rem', marginBottom: '1rem' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.25rem' }}>📋 Info Request History</h2>
                <p style={{ color: '#64748B', fontSize: '0.9rem', marginTop: '0.5rem', margin: '0.5rem 0 0 0' }}>
                  Vehicle: <strong>{infoHistoryModal.repair.vehicle_no}</strong> • {infoHistoryModal.repair.description}
                </p>
              </div>
              <button
                onClick={() => setInfoHistoryModal({ show: false, repair: null })}
                style={{
                  background: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem'
                }}
              >
                ✕ Close
              </button>
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: '0.5rem' }}>
              {(() => {
                const repair = infoHistoryModal.repair;
                const infoResponses = repair.info_responses || [];
                const pendingRequest = repair.additional_info_requested;
                const totalRequests = infoResponses.length + (pendingRequest ? 1 : 0);
                
                if (totalRequests === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                      <p style={{ fontSize: '1.1rem' }}>No information requests have been made for this repair.</p>
                      <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                        When an approver requests additional info, the full history will appear here.
                      </p>
                    </div>
                  );
                }
                
                // Build a combined timeline
                const timeline = [];
                
                // Add all completed request-response pairs
                infoResponses.forEach((response, idx) => {
                  timeline.push({
                    type: 'request',
                    data: response.original_request || { reason: 'Information requested', requested_by: 'Unknown', requested_at: response.responded_at },
                    index: idx
                  });
                  timeline.push({
                    type: 'response',
                    data: response,
                    index: idx
                  });
                });
                
                // Add pending request if exists
                if (pendingRequest) {
                  timeline.push({
                    type: 'pending_request',
                    data: pendingRequest,
                    index: infoResponses.length
                  });
                }
                
                return (
                  <div>
                    {/* Summary Stats */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(3, 1fr)', 
                      gap: '1rem', 
                      marginBottom: '1.5rem',
                      padding: '1rem',
                      background: '#F8FAFC',
                      borderRadius: '8px'
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#F97316', margin: 0 }}>{totalRequests}</p>
                        <p style={{ fontSize: '0.75rem', color: '#64748B', margin: 0 }}>Total Requests</p>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10B981', margin: 0 }}>{infoResponses.length}</p>
                        <p style={{ fontSize: '0.75rem', color: '#64748B', margin: 0 }}>Responded</p>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: '1.5rem', fontWeight: '700', color: pendingRequest ? '#DC2626' : '#64748B', margin: 0 }}>
                          {pendingRequest ? 1 : 0}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#64748B', margin: 0 }}>Pending</p>
                      </div>
                    </div>
                    
                    {/* Timeline */}
                    <div style={{ position: 'relative', paddingLeft: '1.5rem' }}>
                      {/* Vertical line */}
                      <div style={{ 
                        position: 'absolute', 
                        left: '6px', 
                        top: '12px', 
                        bottom: '12px', 
                        width: '2px', 
                        background: '#E2E8F0' 
                      }} />
                      
                      {timeline.map((item, idx) => (
                        <div key={idx} style={{ position: 'relative', marginBottom: '1rem' }}>
                          {/* Timeline dot */}
                          <div style={{
                            position: 'absolute',
                            left: '-1.5rem',
                            top: '6px',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            background: item.type === 'pending_request' ? '#DC2626' : 
                                        item.type === 'request' ? '#F97316' : '#10B981',
                            border: '2px solid white',
                            boxShadow: '0 0 0 2px ' + (item.type === 'pending_request' ? '#FEE2E2' : 
                                       item.type === 'request' ? '#FFEDD5' : '#D1FAE5')
                          }} />
                          
                          {item.type === 'request' && (
                            <div style={{ 
                              padding: '1rem', 
                              background: '#FFFBEB', 
                              borderRadius: '8px',
                              border: '1px solid #FDE68A'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                                <span style={{ fontWeight: '600', color: '#92400E', fontSize: '0.85rem' }}>
                                  📤 INFO REQUESTED
                                </span>
                                <span style={{ fontSize: '0.75rem', color: '#64748B' }}>
                                  {new Date(item.data.requested_at).toLocaleString()}
                                </span>
                              </div>
                              <p style={{ margin: '0.5rem 0', color: '#374151' }}>
                                {item.data.reason || 'Additional information requested'}
                              </p>
                              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B' }}>
                                Requested by: <strong>{item.data.requested_by}</strong>
                              </p>
                            </div>
                          )}
                          
                          {item.type === 'response' && (
                            <div style={{ 
                              padding: '1rem', 
                              background: '#F0FDF4', 
                              borderRadius: '8px',
                              border: '1px solid #BBF7D0'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                                <span style={{ fontWeight: '600', color: '#166534', fontSize: '0.85rem' }}>
                                  ✅ RESPONSE PROVIDED
                                </span>
                                <span style={{ fontSize: '0.75rem', color: '#64748B' }}>
                                  {new Date(item.data.responded_at).toLocaleString()}
                                </span>
                              </div>
                              <p style={{ margin: '0.5rem 0', color: '#374151' }}>
                                {item.data.response || 'No text response provided'}
                              </p>
                              {item.data.files_added > 0 && (
                                <div style={{ 
                                  marginTop: '0.75rem', 
                                  padding: '0.5rem', 
                                  background: '#DCFCE7', 
                                  borderRadius: '4px',
                                  fontSize: '0.85rem'
                                }}>
                                  <span style={{ color: '#166534' }}>
                                    📎 <strong>{item.data.files_added}</strong> file(s) attached
                                    {item.data.file_category && ` (Category: ${item.data.file_category})`}
                                  </span>
                                </div>
                              )}
                              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: '#64748B' }}>
                                Responded by: <strong>{item.data.responded_by}</strong>
                              </p>
                            </div>
                          )}
                          
                          {item.type === 'pending_request' && (
                            <div style={{ 
                              padding: '1rem', 
                              background: '#FEF2F2', 
                              borderRadius: '8px',
                              border: '2px solid #FECACA',
                              boxShadow: '0 2px 8px rgba(220, 38, 38, 0.1)'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                                <span style={{ fontWeight: '600', color: '#DC2626', fontSize: '0.85rem' }}>
                                  ⏳ AWAITING RESPONSE
                                </span>
                                <span style={{ fontSize: '0.75rem', color: '#64748B' }}>
                                  {new Date(item.data.requested_at).toLocaleString()}
                                </span>
                              </div>
                              <p style={{ margin: '0.5rem 0', color: '#374151' }}>
                                {item.data.reason || 'Additional information requested'}
                              </p>
                              <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B' }}>
                                Requested by: <strong>{item.data.requested_by}</strong>
                              </p>
                              <div style={{ 
                                marginTop: '0.75rem', 
                                padding: '0.5rem', 
                                background: '#FEE2E2', 
                                borderRadius: '4px',
                                fontSize: '0.85rem',
                                color: '#991B1B'
                              }}>
                                💡 Go to the "Info Required" tab to provide a response
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
            
            <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: '1rem', marginTop: '1rem' }}>
              <button
                onClick={() => setInfoHistoryModal({ show: false, repair: null })}
                style={{ 
                  width: '100%', 
                  padding: '0.75rem',
                  background: '#1E293B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem'
                }}
              >
                ✕ Close History
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          Phase 4: Invoice Tallying Modals
          ===================================================== */}

      {/* Mark Work Complete Modal */}
      {workCompleteModal.show && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => setWorkCompleteModal({ show: false, repairId: null, notes: '' })}
        >
          <div 
            style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              maxWidth: '450px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
              🔧 Mark Work Complete
            </h3>
            <p style={{ color: '#64748B', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Confirm that the repair work has been completed. After this, you can upload the final invoice.
            </p>
            
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ color: '#C8102E', fontWeight: '600' }}>
                📅 Date Vehicle Entered Workshop *
              </label>
              <input
                type="date"
                className="form-input"
                value={workCompleteModal.workshopInDate}
                onChange={(e) => setWorkCompleteModal({ ...workCompleteModal, workshopInDate: e.target.value })}
                required
                style={{ fontSize: '1rem' }}
              />
              <p style={{ fontSize: '0.75rem', color: '#F59E0B', marginTop: '0.25rem' }}>
                ⚠️ Please ensure this date is accurate - it affects revenue loss calculations
              </p>
            </div>
            
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label">Completion Notes (Optional)</label>
              <textarea
                className="form-textarea"
                value={workCompleteModal.notes}
                onChange={(e) => setWorkCompleteModal({ ...workCompleteModal, notes: e.target.value })}
                placeholder="Any notes about the completed work..."
                style={{ minHeight: '80px' }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', flexDirection: isMobile ? 'column' : 'row' }}>
              <button 
                className="btn"
                style={{ background: '#8B5CF6', color: 'white', flex: 1, padding: '0.75rem' }}
                onClick={handleMarkWorkComplete}
              >
                ✓ Confirm Work Complete
              </button>
              <button 
                className="btn btn-secondary"
                style={{ padding: '0.75rem' }}
                onClick={() => setWorkCompleteModal({ show: false, repairId: null, notes: '', workshopInDate: new Date().toISOString().split('T')[0] })}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Invoice Modal */}
      {invoiceModal.show && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => setInvoiceModal({ 
            show: false, 
            repair: null, 
            invoiceNumber: '', 
            invoiceDate: new Date().toISOString().split('T')[0],
            finalAmount: '',
            dueDate: '',
            workshopOutDate: new Date().toISOString().split('T')[0],
            invoiceFile: null 
          })}
        >
          <div 
            style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
              📄 Upload Final Invoice
            </h3>
            
            {invoiceModal.repair && (
              <div style={{ 
                background: '#F8FAFC', 
                padding: '0.75rem', 
                borderRadius: '6px', 
                marginBottom: '1rem',
                fontSize: '0.85rem'
              }}>
                <p style={{ margin: 0 }}>
                  <strong>Vehicle:</strong> {invoiceModal.repair.vehicle_no}
                </p>
                <p style={{ margin: '0.25rem 0' }}>
                  <strong>Approved Amount:</strong> {formatCurrency(invoiceModal.repair.estimated_cost)}
                </p>
              </div>
            )}
            
            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label className="form-label">Invoice Number *</label>
              <input
                type="text"
                className="form-input"
                value={invoiceModal.invoiceNumber}
                onChange={(e) => setInvoiceModal({ ...invoiceModal, invoiceNumber: e.target.value })}
                placeholder="INV-12345"
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Invoice Date *</label>
                <input
                  type="date"
                  className="form-input"
                  value={invoiceModal.invoiceDate}
                  onChange={(e) => setInvoiceModal({ ...invoiceModal, invoiceDate: e.target.value })}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={invoiceModal.dueDate}
                  onChange={(e) => setInvoiceModal({ ...invoiceModal, dueDate: e.target.value })}
                />
              </div>
            </div>
            
            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label className="form-label" style={{ color: '#C8102E', fontWeight: '600' }}>
                📅 Workshop Out Date (Vehicle Released) *
              </label>
              <input
                type="date"
                className="form-input"
                value={invoiceModal.workshopOutDate}
                onChange={(e) => setInvoiceModal({ ...invoiceModal, workshopOutDate: e.target.value })}
                required
                style={{ fontSize: '1rem' }}
              />
              <p style={{ fontSize: '0.75rem', color: '#F59E0B', marginTop: '0.25rem' }}>
                ⚠️ Please ensure this date is accurate - it affects revenue loss & downtime calculations
              </p>
            </div>
            
            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label className="form-label">Final Amount (SGD) *</label>
              <input
                type="number"
                step="0.01"
                className="form-input"
                value={invoiceModal.finalAmount}
                onChange={(e) => setInvoiceModal({ ...invoiceModal, finalAmount: e.target.value })}
                placeholder="0.00"
              />
              {invoiceModal.repair && invoiceModal.finalAmount && (
                (() => {
                  const variance = parseFloat(invoiceModal.finalAmount) - invoiceModal.repair.estimated_cost;
                  if (variance !== 0) {
                    return (
                      <p style={{ 
                        marginTop: '0.25rem', 
                        fontSize: '0.8rem', 
                        color: Math.abs(variance) > 100 ? '#DC2626' : '#F59E0B' 
                      }}>
                        {variance > 0 ? '⬆️' : '⬇️'} Variance: {formatCurrency(variance)} ({((variance / invoiceModal.repair.estimated_cost) * 100).toFixed(1)}%)
                        {Math.abs(variance) > 100 && ' - Will require Senior Manager approval'}
                      </p>
                    );
                  }
                  return null;
                })()
              )}
            </div>
            
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label">Invoice PDF *</label>
              <div style={{ 
                border: '2px dashed #CBD5E1', 
                borderRadius: '6px', 
                padding: '1rem', 
                textAlign: 'center',
                background: invoiceModal.invoiceFile ? '#DCFCE7' : '#F8FAFC',
                cursor: 'pointer'
              }}>
                <input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) setInvoiceModal({ ...invoiceModal, invoiceFile: file });
                  }}
                  style={{ display: 'none' }}
                  id="invoice-file-upload"
                />
                <label htmlFor="invoice-file-upload" style={{ cursor: 'pointer' }}>
                  {invoiceModal.invoiceFile ? (
                    <span style={{ color: '#059669' }}>
                      ✓ {invoiceModal.invoiceFile.name}
                    </span>
                  ) : (
                    <span style={{ color: '#64748B' }}>
                      📄 Click to upload invoice (PDF or image)
                    </span>
                  )}
                </label>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className="btn"
                style={{ background: '#8B5CF6', color: 'white', flex: 1 }}
                onClick={handleUploadInvoice}
                disabled={!invoiceModal.invoiceNumber || !invoiceModal.finalAmount || !invoiceModal.workshopOutDate || !invoiceModal.invoiceFile}
              >
                📤 Upload Invoice
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setInvoiceModal({ 
                  show: false, 
                  repair: null, 
                  invoiceNumber: '', 
                  invoiceDate: new Date().toISOString().split('T')[0],
                  finalAmount: '',
                  dueDate: '',
                  workshopOutDate: new Date().toISOString().split('T')[0],
                  invoiceFile: null 
                })}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Variance Resolution Modal */}
      {varianceModal.show && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => setVarianceModal({ show: false, repair: null, explanation: '', action: 'explain' })}
        >
          <div 
            style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
              ⚠️ Resolve Invoice Variance
            </h3>
            
            {varianceModal.repair && (
              <div style={{ 
                background: '#FEF3C7', 
                padding: '0.75rem', 
                borderRadius: '6px', 
                marginBottom: '1rem',
                fontSize: '0.85rem',
                border: '1px solid #F59E0B'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <p style={{ margin: 0 }}><strong>Vehicle:</strong> {varianceModal.repair.vehicle_no}</p>
                  <p style={{ margin: 0 }}><strong>Approved:</strong> {formatCurrency(varianceModal.repair.estimated_cost)}</p>
                  <p style={{ margin: 0 }}><strong>Invoice:</strong> {formatCurrency(varianceModal.repair.repair?.invoice?.final_amount)}</p>
                  <p style={{ margin: 0, color: (varianceModal.repair.repair?.invoice?.variance || 0) > 0 ? '#DC2626' : '#059669', fontWeight: 'bold' }}>
                    <strong>Variance:</strong> {formatCurrency(varianceModal.repair.repair?.invoice?.variance)}
                  </p>
                </div>
              </div>
            )}
            
            {Math.abs(varianceModal.repair?.repair?.invoice?.variance || 0) > 100 ? (
              <div style={{ 
                background: '#FEE2E2', 
                padding: '0.75rem', 
                borderRadius: '6px', 
                marginBottom: '1rem',
                fontSize: '0.85rem'
              }}>
                <strong>🔴 Variance Over $100</strong>
                <p style={{ margin: '0.25rem 0 0 0' }}>
                  Only Senior Manager or above can approve this variance.
                </p>
              </div>
            ) : (
              <div style={{ 
                background: '#FEF3C7', 
                padding: '0.75rem', 
                borderRadius: '6px', 
                marginBottom: '1rem',
                fontSize: '0.85rem'
              }}>
                <strong>⚠️ Variance Under $100</strong>
                <p style={{ margin: '0.25rem 0 0 0' }}>
                  Please provide an explanation for the variance. Senior Manager will review and close the job.
                </p>
              </div>
            )}
            
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label">
                {varianceModal.action === 'approve' ? 'Approval Notes' : 'Explanation for Variance'} *
              </label>
              <textarea
                className="form-textarea"
                value={varianceModal.explanation}
                onChange={(e) => setVarianceModal({ ...varianceModal, explanation: e.target.value })}
                placeholder={varianceModal.action === 'approve' 
                  ? 'Reason for approving this variance...' 
                  : 'Explain why the final invoice differs from the approved quote...'}
                style={{ minHeight: '100px' }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {['senior_manager', 'ceo', 'admin'].includes(user?.role) ? (
                <>
                  <button 
                    className="btn"
                    style={{ background: '#059669', color: 'white', flex: 1 }}
                    onClick={() => {
                      setVarianceModal({ ...varianceModal, action: 'approve' });
                      handleResolveVariance();
                    }}
                    disabled={!varianceModal.explanation.trim()}
                  >
                    ✓ Approve & Close Job
                  </button>
                  <button 
                    className="btn"
                    style={{ background: '#DC2626', color: 'white' }}
                    onClick={() => {
                      setVarianceModal({ ...varianceModal, action: 'reject' });
                      handleResolveVariance();
                    }}
                    disabled={!varianceModal.explanation.trim()}
                  >
                    ✗ Reject Invoice
                  </button>
                </>
              ) : (
                <button 
                  className="btn"
                  style={{ background: '#F59E0B', color: 'white', flex: 1 }}
                  onClick={handleResolveVariance}
                  disabled={!varianceModal.explanation.trim()}
                >
                  📝 Submit Explanation
                </button>
              )}
              <button 
                className="btn btn-secondary"
                onClick={() => setVarianceModal({ show: false, repair: null, explanation: '', action: 'explain' })}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Supplementary Repair Modal */}
      {supplementaryModal.show && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => setSupplementaryModal({ show: false, repairId: null, description: '', estimatedCost: '', quoteFiles: [] })}
        >
          <div 
            style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
              ➕ Add Supplementary Work
            </h3>
            <p style={{ color: '#64748B', marginBottom: '1rem', fontSize: '0.85rem' }}>
              Additional work discovered during the repair. This will create a new repair record linked to the original job.
            </p>
            
            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label className="form-label">Description of Additional Work *</label>
              <textarea
                className="form-textarea"
                value={supplementaryModal.description}
                onChange={(e) => setSupplementaryModal({ ...supplementaryModal, description: e.target.value })}
                placeholder="Describe the additional repair work needed..."
                style={{ minHeight: '80px' }}
              />
            </div>
            
            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label className="form-label">Estimated Cost (SGD) *</label>
              <input
                type="number"
                step="0.01"
                className="form-input"
                value={supplementaryModal.estimatedCost}
                onChange={(e) => setSupplementaryModal({ ...supplementaryModal, estimatedCost: e.target.value })}
                placeholder="0.00"
              />
            </div>
            
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label">Quote Files (Optional)</label>
              <input
                type="file"
                accept=".pdf,image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  setSupplementaryModal({ ...supplementaryModal, quoteFiles: files });
                }}
                className="form-input"
              />
              {supplementaryModal.quoteFiles.length > 0 && (
                <p style={{ fontSize: '0.8rem', color: '#64748B', marginTop: '0.25rem' }}>
                  {supplementaryModal.quoteFiles.length} file(s) selected
                </p>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className="btn"
                style={{ background: '#4F46E5', color: 'white', flex: 1 }}
                onClick={handleCreateSupplementary}
                disabled={!supplementaryModal.description.trim() || !supplementaryModal.estimatedCost}
              >
                ➕ Create Supplementary Repair
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setSupplementaryModal({ show: false, repairId: null, description: '', estimatedCost: '', quoteFiles: [] })}
              >
                Cancel
              </button>
            </div>
            
            <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#64748B', fontStyle: 'italic' }}>
              Note: The supplementary repair will need to go through the normal approval process.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Approvals;
